import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';

import { SurfaceShell } from '../../../components/SurfaceShell.tsx';
import { Panel } from '../../../components/Panel.tsx';
import { Button } from '../../../components/Button.tsx';
import {
  DataTable,
  MeterCell,
  type Column,
  type SortState,
} from '../../../components/DataTable.tsx';
import { StatusDot } from '../../../components/StatusDot.tsx';
import { Timestamp } from '../../../components/Timestamp.tsx';
import { Sparkline } from '../../../components/Sparkline.tsx';
import { Chip } from '../../../components/Chip.tsx';
import { CheckBox, SearchInput } from '../../../components/Field.tsx';
import {
  SegmentedControl,
  Toolbar,
  ToolbarDivider,
  ToolbarLabel,
  ToolbarSpacer,
} from '../../../components/Toolbar.tsx';
import { Icon } from '../../../shell/Icon.tsx';
import { LIVENESS_STATE, STATUS_STATE, matrixTone, type Tone } from '../../../components/state.ts';
import {
  BATTERY_BAD,
  BATTERY_WARN,
  CERT_BAD_DAYS,
  CERT_WARN_DAYS,
  SEEN_BAD_MS,
  SEEN_WARN_MS,
} from '../../../components/fleetMetrics.ts';
import { NO_VALUE, duration, latlon, num, utc } from '../../../components/format.ts';
import { Unit } from '../../../components/Unit.tsx';
import { RobotLink } from '../../../components/AppLink.tsx';
import { useNow } from '../../../components/useNow.ts';
import { apiFetch } from '../../../auth/apiFetch.ts';
import {
  useRobots,
  type EnrollmentToken,
  type Liveness,
  type Robot,
  type RobotStatus,
} from '../../../hooks/useRobots.ts';
import type { TelemetryReading } from '../../../hooks/useTelemetry.ts';
import {
  Blank, Figure, ReadBody, ReadTag, SkeletonTable, figure, unread, useRead,
  type SkeletonColumn,
} from '../../../components/states/index.ts';
import { useFleetRoute } from '../../../router/useFleetRoute.ts';
import { NewRobotDialog } from './NewRobotDialog.tsx';
import { EnrollmentTokenDialog } from './EnrollmentTokenDialog.tsx';
import styles from './RobotList.module.css';

/* ==========================================================================
   /fleet/list — the fleet ledger.

   The workhorse surface: 28 robots today, hundreds later, open all day. It is
   a data grid, not a page of cards, and every decision here is about keeping
   40 rows scannable:

     · Row state is a 2px leading edge bar plus a 6px dot (THE BAR §5's
       encoding hierarchy, levels 3 and 4). No pills — a badge that repeats
       per row is tell #2. Neither mark is drawn on a nominal row: twenty-two
       grey dots down a column is the ink that stops the six that matter from
       being seen.
     · EVERY STATE WORD CARRIES A CLOCK. `Error 11h`, `Maintenance 6h46m`,
       `Offline 7h39m`, `Provisioning 150d`, `Live 12s`. A word from an enum
       says what; the second question is always how long, and a table that
       answers it in a `Last seen` column a thousand pixels away has not
       answered it. The duration sits at the trailing edge of the cell that
       owns the word, which is the slack a column sized for `Maintenance`
       already had on every row reading `Idle` (THE BAR §13 tell #19).
     · The column SET is a saved view, not a checkbox list. `Operator` answers
       "what is the fleet doing"; `Developer` answers "what software is on it
       and is its identity good". Touching the column menu forks a `Custom`
       view — the Auterion `auterion-flights-log-table.png` model, which is
       the only one that survives two audiences on one table.
     · Every filter clause is a removable chip above the grid, so the active
       query is legible and each clause is dismissible on its own.
     · Nothing on screen is derived beyond a threshold that is published in
       the UI. The sparkline is the robot's own telemetry window; the battery
       meter is the robot's own reading; the build column is the revision the
       robot is pinned to and the sha256 of that revision's bringup script.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Thresholds. All of them are imported from fleetMetrics so the list, the
   matrix and the exception rail can never disagree about who is in trouble;
   the two below are the ones the list adds.
   -------------------------------------------------------------------------- */

/* ONE COLOUR LAW, EVERY NUMERIC COLUMN.
   Battery, CPU, temperature and disk are all bounded quantities read against
   a published limit, so all four are drawn by the same primitive with the
   same rule: steel inside the limits, amber at `warn`, red past `bad`, a 1px
   neutral tick on the track at the hard limit, and the readout recolouring
   in place with the fill. A column of grey numbers beside a column that
   recolours teaches the operator that only one of them is instrumented. */

/** CPU load, matching the robot-detail header meter. High is bad. */
const CPU_WARN = 75;
const CPU_BAD = 90;

/** Board temperature. The contract carries no thermal envelope — no
 *  morphology revision publishes one — so the console publishes its own and
 *  prints it on the track rather than hiding it in a lint rule: 70 °C is
 *  where an Orin throttles, 85 °C is where it stops. The domain is 0–100 °C
 *  because that is the span a board temperature lives in, and a fixed domain
 *  is what makes the bar's length comparable down the column. */
const TEMP_WARN = 70;
const TEMP_BAD = 85;
const TEMP_MAX = 100;

/** Disk. A robot that fills its disk stops recording the session it was sent
 *  out to record, which is a fault nobody sees until the capture is missing. */
const DISK_WARN = 80;
const DISK_BAD = 92;

/** Received signal strength on the robot's uplink, in dBm. The contract puts
 *  it in the reading's `payload` and publishes no thresholds, so the console
 *  publishes its own and prints them on the track: −80 dBm is where a link
 *  starts retransmitting, −90 is where it stops carrying video. The domain is
 *  the span a 2.4/5GHz link lives in, so the bar's length is comparable down
 *  the column rather than fitted per row. Low is bad. */
const RSSI_MIN = -95;
const RSSI_MAX = -35;
const RSSI_WARN = -80;
const RSSI_BAD = -90;

/** The uplink RSSI carried in a reading's free-form payload, if this agent
 *  reports one. Never coerced: a payload without the key is no reading, and a
 *  meter with no reading draws a hatched track rather than a zero. */
function rssiOf(t: TelemetryReading | null | undefined): number | null {
  const v = t?.payload['link_rssi_dbm'];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** ONE FRESHNESS LAW FOR THE WHOLE ROW.
 *
 *  A telemetry-derived value is only shown while the reading behind it is
 *  still current. Past this it reads `—` over a hatched track, because a
 *  90-minute-old speed rendered as a live number is the console lying.
 *
 *  This governs the TRACE as well as the numbers. A sparkline is a window
 *  ending NOW: a 32-sample battery curve for a robot that last spoke seven
 *  hours ago is not a trend, it is an archive, and drawing it beside four
 *  hatched meters in the same row taught the reader that only some of the
 *  row obeyed the rule. Where the newest sample is older than this, the
 *  trend column draws the same hatched no-data track its neighbours draw. */
const READING_FRESH_MS = SEEN_WARN_MS;

/** Samples pulled per robot for the trend column. The window this buys is
 *  printed in the column header — a decimated view says so (THE BAR §12.10). */
const TREND_SAMPLES = 32;

const DAY_MS = 86_400_000;

/* --------------------------------------------------------------------------
   Fleet-wide telemetry.

   The API has no fleet-scoped telemetry-series route, so a trend column costs
   one request per robot. They are only issued while a telemetry-backed column
   is actually on screen, and they poll a quarter as often as the fleet list.
   -------------------------------------------------------------------------- */

interface Series {
  values: (number | null)[];
  times: string[];
  /** The newest reading, if it is still current. Null once it is history —
      a 90-minute-old speed rendered as a live number is the console lying. */
  fresh: TelemetryReading | null;
  spanMs: number | null;
  /** Age of the NEWEST sample in the window. Null when there is no window at
      all. This is what decides whether the trace is a trend or an archive,
      and it is the same number `fresh` is gated on — one fact, one rule. */
  ageMs: number | null;
}

const NO_SERIES: Series = { values: [], times: [], fresh: null, spanMs: null, ageMs: null };

/** Is this window a claim about NOW? The whole row hangs off this one call:
    speed, CPU, temperature and disk print `—` when it is false, and the
    trend column draws a hatched track rather than a curve. */
function isCurrent(s: Series): boolean {
  return s.ageMs !== null && s.ageMs <= READING_FRESH_MS;
}

async function fetchWindow(robotId: string): Promise<TelemetryReading[]> {
  const r = await apiFetch(
    `/api/robots/${encodeURIComponent(robotId)}/telemetry?limit=${TREND_SAMPLES}`,
  );
  if (!r.ok) throw new Error(`status ${r.status}`);
  const body = (await r.json()) as { readings?: TelemetryReading[] };
  return body.readings ?? [];
}

/* ==========================================================================
   TIME IN STATE.

   `Error` is not an answer. `Error 11h` is.

   A word from an enum tells an operator WHAT is wrong; the second question,
   every time, is HOW LONG — a robot that faulted four minutes ago is a robot
   somebody is already looking at, and one that faulted eleven hours ago is a
   robot nobody is. Those two rows are indistinguishable on a table that
   prints the word alone, and `Last seen 18s ago` does not close the gap: link
   freshness is not fault age. koko-04 has been faulted since 04:51Z and has
   been talking to us the whole time.

   So both state columns carry a duration, as a dim mono suffix pinned to the
   cell's trailing edge — the ~95px of slack a column sized for `Maintenance`
   already had on every row that says `Idle`. It costs no new column, and it
   is `skydio-dfr-incident-dispatch.png`'s right-aligned age chip and
   `auterion-vehicle-detail-overview.png`'s `Offline · 3 years ago` (THE BAR
   §13 tell #19) applied to a 28-row grid instead of a card.

   The two columns get their clocks from two different places, because the two
   facts are recorded in two different places:

     LINK   — the fleet record. Liveness is derived server-side from telemetry
              recency, so the duration behind the word is the raw measurement
              the word was derived FROM: how long the robot has been silent.
              Free; already in the row.

     STATUS — the telemetry series. The record carries no `status_changed_at`
              (`updated_at` moves on every reading), so the onset is found by
              walking back through the robot's own reported `state` until it
              changes. That is one wide request per faulted robot, which is
              why it is issued for those rows ONLY. See the note on
              STATE_SCAN_LIMIT.
   ========================================================================== */

/** The statuses that owe the reader a duration. A nominal row does not: the
    fleet is mostly nominal, and `Active 2h05m` on twenty-two rows would bury
    the six that matter under the ink meant to find them. Same argument as the
    row's leading edge bar, which is also absent on a healthy row. */
const TIMED_STATUS: ReadonlySet<RobotStatus> = new Set<RobotStatus>(['error', 'maintenance']);

/** How far back the console will look for the moment a robot entered its
    current mode. 600 readings is the contract's own ceiling on this route and
    buys ~24h at the agent's reporting cadence; past that the cell prints a
    floor (`>24h`) rather than a number, because a bound is honest and an
    extrapolation is not. */
const STATE_SCAN_LIMIT = 600;

/** Fan-out ceiling on the scan. One request per faulted robot is cheap on a
    fleet with four faults and is not cheap on a fleet with four hundred, so
    the console stops asking rather than melting the tab; rows past the
    ceiling print the word with no duration, and say why. */
const STATE_SCAN_MAX = 48;

interface StateRun {
  /** The mode the newest reading reports. Compared against the record's own
      status before the duration is shown: if the two disagree the console has
      no business dating a transition it cannot see. */
  state: string;
  /** The OLDEST reading in the unbroken run — the earliest moment the robot is
      known to have been in this mode. */
  sinceIso: string;
  /** The NEWEST reading — when the mode was last confirmed by the robot
      itself, which on an offline robot is hours behind now. */
  confirmedIso: string;
  samples: number;
  /** The run reached the end of the window without changing, so `sinceIso` is
      a lower bound and the readout wears a `>`. */
  atLeast: boolean;
}

/** Walk back from the newest reading while the reported mode holds. */
async function fetchStateRun(robotId: string): Promise<StateRun | null> {
  const r = await apiFetch(
    `/api/robots/${encodeURIComponent(robotId)}/telemetry?limit=${STATE_SCAN_LIMIT}`,
  );
  if (!r.ok) throw new Error(`status ${r.status}`);
  const body = (await r.json()) as { readings?: TelemetryReading[] };
  const readings = body.readings ?? [];
  const head = readings[0];
  if (head === undefined || head.state === null) return null;
  const state = head.state;
  let i = 1;
  while (i < readings.length && readings[i]?.state === state) i += 1;
  const oldest = readings[i - 1];
  if (oldest === undefined) return null;
  return {
    state,
    sinceIso: oldest.ts,
    confirmedIso: head.ts,
    samples: i,
    atLeast: i === readings.length,
  };
}

/* --------------------------------------------------------------------------
   Build provenance.

   A robot is pinned to a hardware revision; the revision carries the bringup
   script that produced the machine. `rev-c d739cb` in the row is the version
   label plus the first six of that script's sha256 — Auterion's
   `Software Version` + `Commit` pair, in one cell. A robot still on the
   provisional `001` pin has no manifest at all, and that is an exception the
   fleet list must show rather than hide on a catalog page.
   -------------------------------------------------------------------------- */

interface RevisionRow {
  id: number;
  version: string;
  notes: string | null;
  complete: boolean;
  bringup_ready: boolean;
  script_sha256: string | null;
  meta: Record<string, unknown>;
}

async function fetchRevisions(morphology: string): Promise<RevisionRow[]> {
  const r = await apiFetch(`/api/morphologies/${encodeURIComponent(morphology)}/versions`);
  if (!r.ok) throw new Error(`status ${r.status}`);
  const body = (await r.json()) as { versions?: RevisionRow[] };
  return body.versions ?? [];
}

/* --------------------------------------------------------------------------
   Row helpers
   -------------------------------------------------------------------------- */

function metaString(r: Robot, key: string): string | null {
  const v = r.meta[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function certDays(r: Robot, now: number): number | null {
  if (r.cert_not_after == null) return null;
  const t = new Date(r.cert_not_after).getTime();
  return Number.isNaN(t) ? null : (t - now) / DAY_MS;
}

function seenAgeMs(r: Robot, now: number): number | null {
  if (r.last_seen == null) return null;
  const t = new Date(r.last_seen).getTime();
  return Number.isNaN(t) ? null : now - t;
}

/** The clock behind the Link word.
 *
 *  Liveness is a reading OF this number — the server derives `live` /
 *  `backfilling` / `stale` / `offline` from how long the robot has been
 *  silent — so printing the number beside the word is not a second fact, it
 *  is the first one with its units restored.
 *
 *  `provisioning` is the one row where the clock is a different clock, and it
 *  has to be: a robot that has never phoned home has no `last_seen` at all,
 *  and the only honest duration is how long it has been on the books waiting
 *  to. The cell's own title says which. */
function linkSince(r: Robot): { iso: string; what: string } | null {
  if (r.liveness === 'provisioning') {
    const iso = r.enrolled_at ?? r.created_at;
    return iso == null ? null : { iso, what: 'registered' };
  }
  return r.last_seen == null ? null : { iso: r.last_seen, what: 'silent for' };
}

/* The row's leading 2px edge bar answers one question — does this row need a
   human — so it takes the WORSE of the two independent facts a robot carries.
   A robot that is idle but unreachable must not paint the same steel bar as
   one that is idle and live; fusing them in the *word* would be wrong (the
   Status and Link columns still say both), fusing them in the alarm channel
   is the whole point of an alarm channel. */
const TONE_SEVERITY: Partial<Record<Tone, number>> = {
  /* Held beats unreachable on purpose: a robot a human took out of service is
     expected to stop answering, and naming the decision is more use than
     naming its consequence. Both words are still in the row. */
  error: 4, maintenance: 3, offline: 2, warn: 1,
};

function rowEdgeTone(r: Robot): Tone | null {
  const a = matrixTone(STATUS_STATE[r.status].tone);
  const b = matrixTone(LIVENESS_STATE[r.liveness].tone);
  const worst = (TONE_SEVERITY[a] ?? 0) >= (TONE_SEVERITY[b] ?? 0) ? a : b;
  /* A nominal row gets NO bar. Twenty-two steel bars down the leading edge is
     ink carrying no information, and it is what stops the six that mean
     something from being visible from across the room. */
  return worst === 'nominal' ? null : worst;
}

/* Whether a reading carried in the fleet RECORD is still a claim about NOW.

   Measured off `last_seen`, not off the liveness enum, and that distinction
   turned out to matter: `backfilling` is a robot uploading backdated data,
   which the enum treats as healthy, while koko-03 wearing that word had in
   fact last spoken twenty-four minutes ago. The row's stream columns —
   speed, CPU, temperature, disk — were all reading `—` off the sample age
   while Battery beside them printed a bright, unqualified `58%` off the
   enum. Two clocks in one row is one clock too many, so there is now one:
   the age of the newest thing this robot said. */
function recordIsCurrent(r: Robot, now: number): boolean {
  const age = seenAgeMs(r, now);
  return age !== null && age <= READING_FRESH_MS;
}

/* Sort ranks. Alphabetical order on a state enum is meaningless; ascending
   here means "worst first", which is the direction an operator wants. */
const STATUS_RANK: Record<RobotStatus, number> = {
  error: 0, maintenance: 1, charging: 2, active: 3, idle: 4,
};
const LIVENESS_RANK: Record<Liveness, number> = {
  offline: 0, provisioning: 1, stale: 2, backfilling: 3, live: 4,
};

/** One rank step, in the same units as the durations packed underneath it.
    Comfortably longer than any age the console can hold (317 years), so a
    rank can never be reached by an age. */
const SORT_RANK_STEP = 1e13;

/** How long the robot has been in the mode the record reports, if the console
    can see it. Same gate as the cell: no run, or a run for a different mode,
    is no number. */
function modeAgeMs(
  r: Robot,
  runs: ReadonlyMap<string, StateRun>,
  now: number,
): number | null {
  const run = runs.get(r.robot_id);
  if (run === undefined || run.state !== r.status) return null;
  const t = new Date(run.sinceIso).getTime();
  return Number.isNaN(t) ? null : Math.max(0, now - t);
}

/** The duration the Link cell prints. */
function linkAgeMs(r: Robot, now: number): number | null {
  const since = linkSince(r);
  if (since === null) return null;
  const t = new Date(since.iso).getTime();
  return Number.isNaN(t) ? null : Math.max(0, now - t);
}

/* --------------------------------------------------------------------------
   Facets
   -------------------------------------------------------------------------- */

type FacetKey = 'flag' | 'status' | 'link' | 'morphology' | 'site';

const FACET_KEYS: readonly FacetKey[] = ['flag', 'status', 'link', 'morphology', 'site'];

const FACET_LABEL: Record<FacetKey, string> = {
  flag: 'Attention',
  status: 'Status',
  link: 'Link',
  morphology: 'Morphology',
  site: 'Site',
};

type Filters = Readonly<Record<FacetKey, ReadonlySet<string>>>;

const NO_FILTERS: Filters = {
  flag: new Set(), status: new Set(), link: new Set(), morphology: new Set(), site: new Set(),
};

interface FlagDef {
  value: string;
  label: string;
  /** The word the chip carries. Short, because a chip is a summary of the
      clause, not a restatement of the menu row. */
  short: string;
  tone: Tone;
  test: (r: Robot, now: number) => boolean;
}

/* Every clause publishes the threshold it fires on, in its own label. A
   filter called "Low battery" with the number only in the code is THE BAR
   tell #15 wearing a different hat. */
const FLAGS: readonly FlagDef[] = [
  {
    value: 'fault',
    short: 'faulted',
    label: 'Faulted',
    tone: 'error',
    test: (r) => r.status === 'error',
  },
  {
    value: 'held',
    short: 'held',
    label: 'Held out of service',
    tone: 'maintenance',
    test: (r) => r.status === 'maintenance',
  },
  {
    value: 'lowbatt',
    short: 'low battery',
    label: `Battery under ${BATTERY_WARN}%`,
    tone: 'warn',
    test: (r) => r.battery_pct != null && r.battery_pct <= BATTERY_WARN,
  },
  {
    value: 'notcurrent',
    short: 'not current',
    label: `No telemetry for ${Math.round(SEEN_WARN_MS / 60_000)}m`,
    tone: 'warn',
    test: (r, now) => {
      const age = seenAgeMs(r, now);
      return age === null || age > SEEN_WARN_MS;
    },
  },
  {
    value: 'cert',
    short: 'cert expiring',
    label: `Certificate under ${CERT_WARN_DAYS}d`,
    tone: 'warn',
    test: (r, now) => {
      const d = certDays(r, now);
      return d !== null && d <= CERT_WARN_DAYS;
    },
  },
  {
    value: 'never',
    short: 'never reported',
    label: 'Never reported',
    tone: 'offline',
    test: (r) => r.liveness === 'provisioning',
  },
];

/** What a clause's value is called on the chip. A status or a site is a raw
    machine string and stays one; an attention flag is an internal key and has
    to be spelled out. */
function clauseWord(key: FacetKey, value: string): string {
  if (key !== 'flag') return value;
  return FLAGS.find((f) => f.value === value)?.short ?? value;
}

const STATUS_ORDER: readonly RobotStatus[] = ['active', 'idle', 'charging', 'error', 'maintenance'];
const LIVENESS_ORDER: readonly Liveness[] = ['live', 'backfilling', 'stale', 'offline', 'provisioning'];

/** Every value of a facet that a given robot carries. `flag` is multi-valued;
    the rest hold exactly one. */
function facetValues(key: FacetKey, r: Robot, now: number): string[] {
  switch (key) {
    case 'flag': return FLAGS.filter((f) => f.test(r, now)).map((f) => f.value);
    case 'status': return [r.status];
    case 'link': return [r.liveness];
    case 'morphology': return [r.morphology];
    case 'site': return [metaString(r, 'site') ?? NO_VALUE];
  }
}

/* --------------------------------------------------------------------------
   Columns
   -------------------------------------------------------------------------- */

type ColKey =
  | 'robot' | 'morphology' | 'model' | 'board'
  | 'status' | 'link' | 'rssi' | 'battery' | 'trend' | 'speed' | 'cpu' | 'temp' | 'disk' | 'last_seen'
  | 'site' | 'bay' | 'position' | 'heading'
  | 'agent' | 'build'
  | 'cert' | 'enrolled_by' | 'enrolled';

type ColGroup = 'Identity' | 'Live' | 'Location' | 'Build' | 'Registration';

const COL_GROUPS: readonly ColGroup[] = ['Identity', 'Live', 'Location', 'Build', 'Registration'];

interface ColMeta {
  key: ColKey;
  group: ColGroup;
  /** The name in the column menu, where there is no header to read. */
  name: string;
  /** Needs the fleet-wide telemetry window. */
  telemetry?: boolean;
  /** Why a column is off by default, where the reason is not obvious. An
      operator who cannot find `Last seen` has to be told where it went, in
      the control that took it away — not left to search for it. */
  note?: string;
}

const COL_META: readonly ColMeta[] = [
  { key: 'robot', group: 'Identity', name: 'Robot' },
  { key: 'morphology', group: 'Identity', name: 'Morphology' },
  { key: 'model', group: 'Identity', name: 'Model' },
  { key: 'board', group: 'Identity', name: 'Board' },
  { key: 'status', group: 'Live', name: 'Status' },
  { key: 'link', group: 'Live', name: 'Link' },
  { key: 'rssi', group: 'Live', name: 'Link RSSI', telemetry: true },
  { key: 'battery', group: 'Live', name: 'Battery' },
  { key: 'trend', group: 'Live', name: 'Trend', telemetry: true },
  { key: 'speed', group: 'Live', name: 'Speed', telemetry: true },
  { key: 'cpu', group: 'Live', name: 'CPU', telemetry: true },
  { key: 'temp', group: 'Live', name: 'Temperature', telemetry: true },
  { key: 'disk', group: 'Live', name: 'Disk used', telemetry: true },
  { key: 'last_seen', group: 'Live', name: 'Last seen', note: 'in Link' },
  { key: 'site', group: 'Location', name: 'Site' },
  { key: 'bay', group: 'Location', name: 'Bay' },
  { key: 'position', group: 'Location', name: 'Position' },
  { key: 'heading', group: 'Location', name: 'Heading' },
  { key: 'agent', group: 'Build', name: 'Agent' },
  { key: 'build', group: 'Build', name: 'Build' },
  { key: 'cert', group: 'Registration', name: 'Cert expires' },
  { key: 'enrolled_by', group: 'Registration', name: 'Enrolled by' },
  { key: 'enrolled', group: 'Registration', name: 'Enrolled' },
];

const COL_ORDER = COL_META.map((c) => c.key);

/* ==========================================================================
   THE COLUMN LAYOUT IS A PROPERTY OF THE VIEW AND THE VIEWPORT. FULL STOP.

   It used to be a property of the ROWS: the table measured the ink of every
   cell and sized each column to its widest value, so typing `koko` into the
   search box moved eight headers, narrowed Robot by 53px and materialised a
   `Bay` column that the full fleet did not have. That is a table that
   rearranges itself under an operator who is mid-scan, and it makes the one
   thing a grid is for — a stable x-position per quantity — unavailable.

   So every column below carries a PINNED px width, sized once, off the widest
   string the API contract can ever put in it (`Maintenance`, `Provisioning`,
   `fremont-facility`, `ops-oncall@forest3.ai`, `10h 58m ago`, a 48px meter
   track + `100 °C`) or off its own header, whichever is wider. The widths do
   not consult the rows. 28 robots and 4 robots draw the same grid.

   Exactly one column is elastic — `robot`, the scanning column — and it takes
   whatever the viewport has left over. That is deliberate: the surplus has to
   go SOMEWHERE (a table narrower than its panel leaves a dead band that the
   row hover still paints), and the identity column is the only one where more
   width is more readable rather than more void. Its floor is `ROBOT_BASE`;
   the breakpoints below are chosen so it lands between ~195 and ~260px at
   every width the console is used at, because the moment it would grow past
   that, another column fits and is worth more than the whitespace.
   ========================================================================== */

/** Pinned width, in px, including the cell's 2 × 10px insets. `trend` is
    absent because its width is computed from the metric on display. */
const COL_W: Record<Exclude<ColKey, 'robot' | 'trend'>, number> = {
  morphology: 102, model: 116, board: 96,
  /* THE TWO STATE COLUMNS ARE SIZED FOR A WORD *AND* A CLOCK.

     `Maintenance` (84px with its dot) and `Provisioning` (91px) are the
     longest strings either vocabulary can print; each now carries a 5ch mono
     duration pinned to the cell's trailing edge, so the width is
     `dot + widest word + 5px + 5ch + 2 × 10px inset`. That reserve is what
     the column had been spending as void on every row reading `Idle` — sized
     for `Maintenance`, filled with `Live`.
     Battery is a plain meter again — track + readout — now that the age it
     used to stamp ahead of the track is carried by the Link cell. */
  status: 145, link: 152, rssi: 130, battery: 110,
  speed: 86, cpu: 106, temp: 122, disk: 106, last_seen: 96,
  site: 128, bay: 58, position: 158, heading: 60,
  agent: 86, build: 104,
  cert: 116, enrolled_by: 162, enrolled: 92,
};

/** The floor the elastic identity column may not go below, and the width the
    shed test reserves for it. A hair over the `identity` kind's own declared
    base (26ch of the table's 12px Inter), so the resolver always has surplus
    to hand out rather than falling through to a horizontal scrollbar. */
const ROBOT_BASE = 196;
/** DataTable's own two fixed tracks: the selection checkbox and the trailing
    chevron gutter. Neither takes part in the distribution. */
const CHROME_PX = 26 + 22;
/** Held back from the surplus so a stale width measurement — the frame after
    a resize, or one where a scrollbar has just appeared — can never push the
    table past its wrapper and grow a horizontal scrollbar. */
const SLACK_SAFETY = 8;

/* Which columns turn surplus width into readability rather than into void.
   Left-aligned strings do; a meter track, a right-aligned number and a
   relative age do not — their content has one true width and the space would
   open as a gap between the previous column and this one's own right edge.
   `robot` is not listed: it is the residual claimant and takes what is left.

   `status` and `link` came OFF this list when they grew a trailing duration:
   a cell that is now `word ……… clock` is already spending its slack as the
   gap between two things, and handing it more just widens that gap on every
   row. Their content has a true width now, so they are pinned to it. */
const COL_GROW: Partial<Record<ColKey, number>> = {
  site: 1,
  morphology: 1, model: 1, board: 1,
  agent: 1, build: 1, enrolled_by: 1,
};
/** The identity column's share, expressed in the same units, so the split is
    one arithmetic rather than a special case. */
const ROBOT_GROW = 3;

type ViewKey = 'operator' | 'developer' | 'custom';

/** A column in a view: where it sits, and how early it is given up when the
    viewport cannot hold everything. `shed` is a priority, not a breakpoint —
    the highest goes first — and a column with none is never shed. */
interface Slot {
  key: ColKey;
  shed?: number;
}

/* Two audiences, two column sets, one table. Only Robot / Status / Link are
   common to both — a preset that changes three columns is a toggle, not a
   view.

   `Operator` answers "what is the fleet doing": the four vitals (charge,
   load, temperature, disk), the trace, where the robot is, and — at the
   widths that can hold them — the agent version and build the readings came
   out of, because at fleet scale provenance is a column, not a detail page.
   `Developer` answers "what software is on it and is its identity good".

   NEITHER CARRIES `Last seen` ANY MORE, and that is a deletion rather than an
   oversight. The Link cell now prints the silence age beside the word it was
   derived from — `Live 12s`, `Offline 7h39m` — so a separate `Last seen`
   column set the same number a second time, right-aligned at the far end of a
   1400px row, and a table that prints one fact twice has taught its reader
   that one of the two copies is the real one. The column is still in the
   catalogue and one tick away for anyone who wants it back; the 96px it
   returned is most of what paid for the two durations.

   The shed order is a judgement about what an operator gives up last, and it
   is decided against how fast the fact underneath it CHANGES. `bay` is the
   first thing given up and `build` outlives it: a parking slot changes once a
   week and is on the detail page, whereas which software produced a robot's
   readings is the question every incident opens with — THE BAR tell #23 puts
   the version and its commit in a COLUMN at fleet scale, not on a page one
   click away. `disk` outlives both: a filling disk is the fault that silently
   costs the session the robot was sent out to record. */
const PRESETS: Record<'operator' | 'developer', readonly Slot[]> = {
  operator: [
    { key: 'robot' },
    { key: 'status' },
    { key: 'link' },
    { key: 'rssi', shed: 9 },
    { key: 'battery' },
    { key: 'trend', shed: 2 },
    { key: 'speed', shed: 5 },
    { key: 'cpu', shed: 4 },
    { key: 'temp', shed: 6 },
    { key: 'disk', shed: 7 },
    { key: 'site', shed: 3 },
    { key: 'bay', shed: 11 },
    { key: 'agent', shed: 10 },
    { key: 'build', shed: 8 },
  ],
  developer: [
    { key: 'robot' },
    { key: 'status' },
    { key: 'link' },
    { key: 'morphology', shed: 5 },
    { key: 'model', shed: 4 },
    { key: 'board', shed: 6 },
    { key: 'agent', shed: 1 },
    { key: 'build', shed: 2 },
    { key: 'cert', shed: 3 },
    { key: 'enrolled_by', shed: 7 },
    { key: 'enrolled', shed: 9 },
    { key: 'site', shed: 8 },
  ],
};

/** Where a column ticked into a `Custom` view sits in the shed order. The
    four the console cannot be read without are never shed; everything else
    goes in roughly the order the presets give it up. */
const CUSTOM_SHED: Partial<Record<ColKey, number>> = {
  last_seen: 1,
  trend: 2, cert: 3, cpu: 4, morphology: 5, speed: 6, model: 6,
  temp: 7, board: 7, disk: 8, site: 9, bay: 10,
  agent: 11, build: 12, rssi: 13, enrolled_by: 14,
  enrolled: 15, position: 16, heading: 17,
};

/** What a column costs. The trend column's width follows its metric, so it is
    passed in rather than tabulated. */
function colWidth(key: ColKey, trendPx: number): number {
  if (key === 'robot') return ROBOT_BASE;
  if (key === 'trend') return trendPx;
  return COL_W[key];
}

interface Layout {
  /** On screen, in display order, each with the px width it will be given.
      `robot` is absent from `width` — it is the residual claimant. */
  cols: readonly { key: ColKey; width: number | null }[];
  /** Ticked in the view but shed because the viewport cannot hold it. */
  dropped: ReadonlySet<ColKey>;
}

/**
 * Resolve a view's slots against the panel's measured width.
 *
 * Two passes, both pure: shed the highest `shed` priority until the rest fit
 * at their own pinned widths, then hand the leftover to the columns that can
 * spend it. Nothing here reads a row.
 */
function resolveLayout(slots: readonly Slot[], avail: number | null, trendPx: number): Layout {
  const cost = (k: ColKey) => colWidth(k, trendPx);

  let kept = slots;
  const dropped = new Set<ColKey>();
  if (avail !== null) {
    let need = CHROME_PX + slots.reduce((a, s) => a + cost(s.key), 0);
    if (need > avail) {
      const order = [...slots]
        .filter((s) => s.shed !== undefined)
        .sort((a, b) => (b.shed ?? 0) - (a.shed ?? 0));
      for (const s of order) {
        if (need <= avail) break;
        dropped.add(s.key);
        need -= cost(s.key);
      }
      kept = slots.filter((s) => !dropped.has(s.key));
    }
  }

  const base = kept.reduce((a, s) => a + cost(s.key), 0);
  const surplus =
    avail === null ? 0 : Math.max(0, avail - CHROME_PX - base - SLACK_SAFETY);
  const units =
    ROBOT_GROW + kept.reduce((a, s) => a + (s.key === 'robot' ? 0 : COL_GROW[s.key] ?? 0), 0);

  return {
    cols: kept.map((s) => {
      /* The identity column is handed no width at all: DataTable's resolver
         gives every pinned column exactly what it asks for and the one
         unpinned claimant absorbs the difference, so the widths always sum to
         the panel and a measurement that is a frame out of date costs nothing
         worse than a few pixels on one column. */
      if (s.key === 'robot') return { key: s.key, width: null };
      const grow = COL_GROW[s.key] ?? 0;
      return {
        key: s.key,
        width: cost(s.key) + (grow === 0 ? 0 : Math.floor((surplus * grow) / units)),
      };
    }),
    dropped,
  };
}

/* --------------------------------------------------------------------------
   Trend metric
   -------------------------------------------------------------------------- */

type TrendKey = 'battery' | 'cpu' | 'temp' | 'speed';

interface TrendDef {
  key: TrendKey;
  label: string;
  unit: string;
  /** The unit a CHANGE in this quantity is measured in, which is not always
      the unit the quantity itself carries. A battery that went 90 → 80 fell
      ten PERCENTAGE POINTS; printing `−10%` two columns from a meter reading
      `80%` invites the reader to hear "ten percent of its charge", which is
      a different and wrong number. Percentages therefore differ from their
      own deltas — `%` → `pp` — while a temperature's delta is still °C and a
      speed's is still m/s. */
  deltaUnit: string;
  digits: number;
  read: (t: TelemetryReading) => number | null;
  /** Widest change readout this metric can print, in `ch` — `−100 pp`,
      `−1.15 m/s`. The column is sized off it, so switching quantity never
      clips the number and never leaves a void where one used to be. */
  deltaCh: number;
  /** THIS QUANTITY IS A RESERVOIR, SO ITS TREND HAS A DEADLINE.
   *
   *  A battery is not a load average: it is a bounded store being drained or
   *  filled, and the only question an operator asks of its trend is "how long
   *  have I got". The change over the window does not answer that — a fleet
   *  discharging at one rate prints one number on every row, which is a
   *  column spending 10% of the table to restate the Status column's
   *  charging-or-not. The RUNWAY does answer it, because it folds in the
   *  level: 80% and 8% at the same rate are nine hours apart.
   *
   *  Only a reservoir gets one. Extrapolating CPU load to 100% would be a
   *  deadline for an event that is not coming, so those metrics keep the
   *  change. The trend menu prints which readout each metric carries. */
  runway?: boolean;
  warn?: number;
  bad?: number;
  invert?: boolean;
  min?: number;
  max?: number;
}

const TRENDS: readonly TrendDef[] = [
  {
    key: 'battery', label: 'Battery', unit: '%', deltaUnit: 'pp', digits: 0, deltaCh: 7,
    read: (t) => t.battery_pct, warn: BATTERY_WARN, bad: BATTERY_BAD, min: 0, max: 100,
    runway: true,
  },
  {
    key: 'cpu', label: 'CPU', unit: '%', deltaUnit: 'pp', digits: 0, deltaCh: 7,
    read: (t) => t.cpu_pct, warn: CPU_WARN, bad: CPU_BAD, invert: true, min: 0, max: 100,
  },
  {
    key: 'temp', label: 'Temp', unit: '°C', deltaUnit: '°C', digits: 0, deltaCh: 7,
    read: (t) => t.temp_c, warn: TEMP_WARN, bad: TEMP_BAD, invert: true, min: 0, max: TEMP_MAX,
  },
  /* Speed has no published ceiling in the contract — no morphology revision
     carries one — so the box is fitted to its own window, the primitive draws
     a dotted floor, and the header says `fit`. A fitted scale never pretends
     to be absolute. */
  {
    key: 'speed', label: 'Speed', unit: 'm/s', deltaUnit: 'm/s', digits: 2, deltaCh: 9,
    read: (t) => t.speed_mps, min: 0,
  },
];

/** The y-domain the trace is drawn against, in the metric's own units. */
function trendScale(t: TrendDef): string {
  if (t.max === undefined) return 'fitted';
  return `${t.min ?? 0}–${t.max}${t.unit.trim()}`;
}

/** What the number beside the trace is, spelled out. Two quantities in one
    column print two different readouts, so the column has to say which — in
    the header's own title, in the metric menu, and in the aria label. */
function readoutName(t: TrendDef): string {
  return t.runway === true
    ? `runway — time to ${t.min ?? 0}${t.unit.trim()} or ${t.max ?? 100}${t.unit.trim()} at the observed rate`
    : 'change across the window';
}

/** The one-word form, for the metric menu's own column. */
function readoutWord(t: TrendDef): string {
  return t.runway === true ? 'runway' : 'change';
}

/** The trace box. 22px so the published limit rule, the trace and the head
    dot each get pixels of their own on the rows where they converge — at 18px
    an 8% battery, its 20% limit and the box floor landed inside four pixels
    and rendered as one red smear.

    64px wide, not 140: THE BAR §5 sizes a table sparkline at 40×14, and the
    column it lives in has to sit beside Battery, CPU and Temp rather than
    outweigh all three. The 76px this gave back is what pays for CPU and Temp
    being instruments instead of grey numbers. */
const TREND_W = 64;
const TREND_H = 22;

/** Trace + gap + change readout + the cell's own 10px insets. Pinned, because
    the trace is a fixed ruler: a box that is 64px on one monitor and 180px on
    another is two different pictures of the same window.

    The column has to hold its own header too — `Trend  Battery 1h14m ⌄` — and
    a truncated header is a column with no name, so the width is the wider of
    the two. Advances measured off the faces the table actually uses: 11px
    JetBrains Mono for the readout, 11px and 10px Inter for the header. */
const TREND_GAP_PX = 5;
const TREND_INSET_PX = 20;
const MONO_CH_PX = 6.6;
const UI_CH_PX = 6.3;
const MICRO_CH_PX = 5.6;
const CHEVRON_PX = 14;
/** Measured slack. The header is a button whose real laid-out width at
    `Trend Battery 1h14m ⌄` is 129px against an estimate of 124; a header that
    overflows its column by five pixels is a header wearing an ellipsis. */
const HEAD_SAFETY_PX = 6;
/** `1h14m`, `12m30s`, `44s` — six characters covers every window this poll
    rate can produce, and reserving it stops the header reflowing as the
    window's own label gets a glyph longer. */
const SPAN_CH = 6;
/** The sort arrow's own track, reserved on every render so sorting by the
    trend never moves the column it sorts. */
const SORT_GLYPH_PX = 15;

/** Widest runway readout: `9h45m` + the dim target `→100%`, plus the thin
    space between them. Everything longer than ten hours collapses to `23h` /
    `>24h`, because a battery with half a day in it is not a number anybody
    reads to the minute. */
const RUNWAY_CH = 11;

/** What this metric prints beside its trace, in `ch`. */
function readoutCh(t: TrendDef): number {
  return t.runway === true ? RUNWAY_CH : t.deltaCh;
}

function trendColPx(t: TrendDef): number {
  const cell = TREND_W + TREND_GAP_PX + readoutCh(t) * MONO_CH_PX;
  const qualifier = t.label.length + 1 + SPAN_CH + (t.max === undefined ? 4 : 0);
  const head =
    'Trend'.length * UI_CH_PX + SORT_GLYPH_PX + 6
    + qualifier * MICRO_CH_PX + CHEVRON_PX + HEAD_SAFETY_PX;
  return Math.round(TREND_INSET_PX + Math.max(cell, head));
}

/** `1h 14m` → `1h14m`. The window is a dim qualifier on a 120px column
    header; the spaced form is for prose and for the tooltip. */
function compactSpan(ms: number): string {
  return duration(ms).replace(/\s+/g, '');
}

/** Oldest and newest real readings in a window. Both ends are actual samples:
    a change quoted between a reading and a gap is not a change. */
function windowEnds(values: readonly (number | null)[]): [number, number] | null {
  let first: number | null = null;
  let last: number | null = null;
  for (const v of values) {
    if (v == null) continue;
    if (first === null) first = v;
    last = v;
  }
  return first === null || last === null ? null : [first, last];
}

/** `+3` / `−12` / `0`. A true minus, and no sign at all on a rounded zero —
    `−0` is a number that does not exist. */
function signed(v: number, digits: number): string {
  const mag = Math.abs(v).toFixed(digits);
  if (Number(mag) === 0) return mag;
  return `${v > 0 ? '+' : '−'}${mag}`;
}

/* --------------------------------------------------------------------------
   Rate, and the deadline it implies.

   The change across a window answers "which way, and how much". For a
   reservoir that is the wrong question: a fleet on one discharge curve prints
   one number on twenty rows, and the number an operator is actually after —
   which robot has to come in first — is not on the screen at all. It is the
   LEVEL divided by the RATE, and it is different on every row.

   Everything below is measured off real samples. Nothing is smoothed, nothing
   is assumed about a robot's duty cycle, and the projection is linear and says
   so: the cell's own tooltip prints the rate, the sample count, the window and
   the arithmetic, so the reader can audit the number rather than trust it.
   -------------------------------------------------------------------------- */

/** Slope of the window, in the metric's own units per hour, by least squares
    over the real samples. Least squares rather than first-to-last because two
    endpoints make the estimate hostage to two readings; the endpoints are
    still printed in the tooltip, so the estimate stays checkable.

    Null under four samples or under a minute of span — a slope quoted off
    three readings a few seconds apart is noise wearing a decimal point. */
const RATE_MIN_SAMPLES = 4;
const RATE_MIN_SPAN_MS = 60_000;

function windowRate(
  values: readonly (number | null)[],
  times: readonly string[],
): number | null {
  let n = 0;
  let sx = 0;
  let sy = 0;
  let sxx = 0;
  let sxy = 0;
  let t0: number | null = null;
  let tn: number | null = null;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    const raw = times[i];
    if (v == null || raw === undefined) continue;
    const t = new Date(raw).getTime();
    if (Number.isNaN(t)) continue;
    if (t0 === null) t0 = t;
    tn = t;
    /* x in hours from the window's first real sample, so the slope comes out
       in units-per-hour with no second conversion to get wrong. */
    const x = (t - t0) / 3_600_000;
    n += 1;
    sx += x;
    sy += v;
    sxx += x * x;
    sxy += x * v;
  }
  if (n < RATE_MIN_SAMPLES || t0 === null || tn === null) return null;
  if (tn - t0 < RATE_MIN_SPAN_MS) return null;
  const denom = n * sxx - sx * sx;
  if (denom === 0) return null;
  return (n * sxy - sx * sy) / denom;
}

/** Below this the window has no slope worth extrapolating, and the cell falls
    back to printing the change. In the metric's units per hour: half a point
    of charge an hour is a battery sitting still. */
const RATE_FLOOR = 0.5;

/** Past a day the answer is "not today" and the minutes are noise. */
const RUNWAY_CAP_MS = 24 * 3_600_000;

interface Runway {
  /** Time until the level reaches `target`, at the observed rate. */
  ms: number;
  /** The bound it is heading for, in the metric's own units. */
  target: number;
  /** Units per hour, signed. */
  rate: number;
}

/** Where this reservoir is heading and when it gets there. Null when the
    quantity is not a reservoir, when the level or the bounds are unknown, or
    when the window is too flat to project — all four of which mean "print the
    change instead", which is a fact rather than a forecast. */
function runwayOf(t: TrendDef, level: number, rate: number | null): Runway | null {
  if (t.runway !== true || rate === null) return null;
  const floor = t.min;
  const ceil = t.max;
  if (floor === undefined || ceil === undefined) return null;
  if (Math.abs(rate) < RATE_FLOOR) return null;
  const target = rate < 0 ? floor : ceil;
  const ms = ((target - level) / rate) * 3_600_000;
  if (!Number.isFinite(ms) || ms <= 0) return null;
  return { ms, target, rate };
}

/** `47m` · `9h45m` · `23h` · `>24h`. Five characters at its widest, which is
    what lets the column reserve for it: a runway is read for its order of
    magnitude first and its minutes second, so the minutes are dropped the
    moment they stop deciding anything. */
function runwayText(ms: number): string {
  if (ms >= RUNWAY_CAP_MS) return '>24h';
  /* Truncated, not rounded, so the cell and the sentence in its own tooltip —
     which is `duration()`, and truncates — can never print two answers a
     minute apart for one projection. */
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 10) return `${h}h${String(m % 60).padStart(2, '0')}m`;
  return `${h}h`;
}

/* --------------------------------------------------------------------------
   A SAVED VIEW THAT IS ACTUALLY SAVED.

   The preset strip calls these saved views, and a view that evaporates on
   reload is a control lying about its own name — an operator who spends a
   minute assembling a `Custom` column set for a bring-up and then hits F5 has
   to assemble it again. So the LAYOUT persists: which preset, the custom
   column set, the trend quantity and the sort.

   The QUERY does not, and that is deliberate. A filter restored from last
   week's session hands the operator a fleet list that is missing rows for a
   reason nothing on screen explains, and the failure mode — "half our robots
   are gone" — is exactly the panic a console exists to prevent. Layout is a
   preference; a filter is a question, and a question is asked fresh.
   -------------------------------------------------------------------------- */

const PREFS_KEY = 'f3.fleet.list.v1';

interface Prefs {
  view: ViewKey;
  custom: readonly Slot[] | null;
  trend: TrendKey;
  sort: SortState;
}

/** Every field is re-validated against the current catalogue on the way in: a
    column or a metric that a later build removed must not be able to reach the
    renderer, and a stored key is untrusted input like any other. */
function loadPrefs(): Partial<Prefs> {
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (raw === null) return {};
    const p = JSON.parse(raw) as Partial<Record<keyof Prefs, unknown>>;
    const out: Partial<Prefs> = {};
    if (p.view === 'operator' || p.view === 'developer' || p.view === 'custom') out.view = p.view;
    if (TRENDS.some((t) => t.key === p.trend)) out.trend = p.trend as TrendKey;
    if (Array.isArray(p.custom)) {
      const slots = (p.custom as unknown[])
        .filter((s): s is Slot =>
          typeof s === 'object' && s !== null
          && COL_ORDER.includes((s as Slot).key))
        .map((s) => (s.shed === undefined ? { key: s.key } : { key: s.key, shed: s.shed }));
      /* Two columns is DataTable's floor and the toggle's floor; anything
         shorter is a corrupt entry, not a view. */
      if (slots.length >= 2) out.custom = slots;
    }
    const so = p.sort as SortState | undefined;
    if (so !== undefined && typeof so.key === 'string' && (so.dir === 'asc' || so.dir === 'desc')) {
      out.sort = { key: so.key, dir: so.dir };
    }
    return out;
  } catch {
    /* A browser with storage disabled gets the defaults and no error: the
       fleet list is not a surface that may fail to render over a preference. */
    return {};
  }
}

function savePrefs(p: Prefs): void {
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    /* ignore — see above */
  }
}

/* --------------------------------------------------------------------------
   Menus
   -------------------------------------------------------------------------- */

type MenuKind = 'filters' | 'columns' | 'trend';

interface MenuState {
  kind: MenuKind;
  left: number;
  top: number;
  width: number;
  anchor: HTMLElement;
}

const MENU_WIDTH: Record<MenuKind, number> = { filters: 468, columns: 236, trend: 168 };

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** A facet row: `[✓] label ……… count ▬▬`. Gotham's rail geometry — 20px
    pitch, right-aligned count, bar proportional to the group's own peak. */
function FacetRow({
  label,
  tone,
  count,
  peak,
  checked,
  title,
  onToggle,
}: {
  label: string;
  tone?: Tone | undefined;
  count: number;
  peak: number;
  checked: boolean;
  title?: string | undefined;
  onToggle: () => void;
}) {
  return (
    <label className={styles.facet} data-empty={count === 0 && !checked} title={title}>
      <CheckBox checked={checked} onChange={onToggle} />
      <span className={styles.facetLabel}>{label}</span>
      <span className={styles.facetCount}>{count}</span>
      {/* Steel unless the facet names something a human has to act on. A bar
          whose LENGTH already encodes the count must not also spend a hue on
          the healthy majority — twelve nominal robots would end up wearing
          the loudest ink in the menu. */}
      <span className={styles.facetTrack} data-tone={tone === undefined ? 'nominal' : matrixTone(tone)}>
        {count > 0 && (
          <span className={styles.facetBar} style={{ width: `${(count / peak) * 100}%` }} />
        )}
      </span>
    </label>
  );
}

/** The same age as a LOWER BOUND — `>24h`, `>6h`, `>45m`.

    Used where the console looked as far back as the contract lets it and the
    state had still not changed: the honest statement is then "at least this
    long", not a number. The minor unit is dropped, because a bound quoted to
    the minute is a bound pretending to be a measurement. */
function coarseFloor(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return `>${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `>${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `>${h}h`;
  return `>${Math.floor(h / 24)}d`;
}

/** What the duration in a state cell is measuring, and from when. */
interface StateClock {
  /** The moment the state is known to have begun, ISO. */
  since: string;
  /** The console could not see past its own window: print a bound, not a
      number. */
  floor?: boolean | undefined;
  /** How the title names this duration — `Error for`, `Last reported`. */
  lead: string;
  /** Whether the duration reads as an age (`… 12s ago`) rather than as a
      span (`… for 11h 00m`). */
  ago?: boolean | undefined;
  /** What introduces the absolute stamp — `since`, or nothing. */
  stampLead?: string | undefined;
  /** Anything the reader needs in order to trust the number: which reading
      dated it, how many samples, when the mode was last confirmed. */
  note?: string | undefined;
}

/** `[dot] Word ………… 7h39m` — one enum column, made into an instrument.
 *
 *  Three renderings, three meanings, and no hue doing the work alone:
 *
 *    no dot          nominal. The word is `Active` / `Live`; nothing to do.
 *    filled dot      a state a human has to act on — the tone names which.
 *    hollow dot      nothing is arriving (offline, never enrolled).
 *
 *  The dot's slot is reserved on every row so the words align down the
 *  column, but a nominal row draws no dot at all. Twenty-two grey dots beside
 *  twenty-two `Live`s is ink with no information in it, and it is exactly what
 *  stops the six that mean something from being visible from across the room —
 *  the same argument this table already makes for the row's leading edge bar.
 *
 *  The duration is pinned to the cell's trailing edge, in the console's dim
 *  step. Grey, always: the row already alarms in the edge bar and in the word,
 *  and a duration is a quantitative fact, not a second alarm. */
function StateCell({
  tone,
  hollow,
  label,
  loud,
  markTone,
  hint,
  clock,
}: {
  tone: Tone;
  hollow: boolean;
  label: string;
  loud: boolean;
  /**
   * Draw the dot in this tone even where the cell is quiet, and leave the word
   * and the clock alone.
   *
   * `loud` is one switch for three things — the mark, the word's colour and the
   * age's colour — which is right for an exception, where all three should
   * shout together. A LIVE LINK is not an exception, so it was silent: nominal
   * projects to steel, `loud` was false, and the cell drew no mark at all. Yet
   * "is this robot connected right now" is the one thing an operator scans a
   * fleet register for, and a word alone makes them read 28 rows to find it.
   * So the mark is separable from the volume — a green dot beside a quiet word.
   */
  markTone?: Tone | undefined;
  hint: string;
  clock?: StateClock | null | undefined;
}) {
  const now = useNow(clock != null);
  let age: { text: string; title: string; iso: string } | null = null;
  if (clock != null) {
    const t = new Date(clock.since).getTime();
    if (!Number.isNaN(t)) {
      const ms = Math.max(0, now - t);
      age = {
        iso: clock.since,
        text: clock.floor === true ? coarseFloor(ms) : duration(ms, { compact: 1 }),
        title:
          `${clock.lead} ${duration(ms)}${clock.ago === true ? ' ago' : ''}`
          + `${clock.floor === true ? ' or longer' : ''}`
          + ` — ${clock.stampLead === undefined ? '' : `${clock.stampLead} `}${utc(clock.since)}`
          + (clock.note === undefined ? '' : `\n${clock.note}`),
      };
    }
  }
  return (
    <span
      className={styles.stateCell}
      data-tone={tone}
      title={age === null ? hint : `${hint}\n${age.title}`}
    >
      <span className={styles.stateMark}>
        {markTone !== undefined
          ? <StatusDot tone={markTone} hollow={hollow} />
          : loud && <StatusDot tone={tone} hollow={hollow} />}
      </span>
      <span className={styles.stateWord} data-loud={loud}>{label}</span>
      {age !== null && (
        <time className={styles.stateAge} data-loud={loud} dateTime={age.iso}>
          {age.text}
        </time>
      )}
    </span>
  );
}

/** A value that rides the fleet record rather than the telemetry stream —
    battery, position, heading. Unlike a stream reading it does not void when
    the robot goes quiet, because the record still holds the last thing the
    robot actually said; it dims, and it stops claiming to be now.

    IT NO LONGER STAMPS ITS OWN AGE, AND THAT IS THE POINT.

    It used to, because the only other copy of the number was in a `Last seen`
    column at the far end of a 1600px row and a dim reading with no age beside
    it is a reading the reader will trust before they cross-check it. The Link
    cell now prints that age three columns to the left, on every row, in the
    cell that owns it — `Offline 7h39m` — and Link is one of the columns this
    table never sheds. Printing `7h39m` again 150px later was the same fact
    twice in one glance, which is the defect the stamp was invented to fix,
    arrived at from the other direction. So the dim survives (it says "not
    now") and the digits live in one place (they say "how much not now"),
    exactly as Position and Heading have always worked.

    A component rather than a bare cell renderer so the dim tracks the
    console's shared 1s beat instead of freezing at the fleet poll. */
function RecordCell({ robot, children }: { robot: Robot; children: ReactNode }) {
  const now = useNow(true);
  const stale = !recordIsCurrent(robot, now);
  return (
    <span className={styles.recordCell}>
      <span className={styles.recordVal} data-stale={stale}>
        {children}
      </span>
    </span>
  );
}

/** A status-bar counter that is also its own filter clause. Same typography
    as the static counts beside it; the affordance is the hover rule, and the
    state is `aria-pressed` plus a 1px accent underline. */
function CountFilter({
  count,
  label,
  tone,
  on,
  onPick,
}: {
  /**
   * `number | string`, so the footer can print `—`. A ZERO IS A MEASUREMENT
   * AND AN UNREAD SOURCE IS NOT: `0 faulted` on a cold start is the single
   * most dangerous string this surface can show, because an operator scanning
   * a wall display reads it as good news.
   */
  count: number | string;
  label: string;
  tone?: Tone | undefined;
  on: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      className={`${styles.footItem} ${styles.footPick}`}
      data-tone={tone}
      aria-pressed={on}
      aria-label={
        typeof count === 'number'
          ? `${on ? 'Clear' : 'Show only'} ${label} — ${count} of the fleet`
          : `${on ? 'Clear' : 'Show only'} ${label} — not read`
      }
      onClick={onPick}
    >
      <span className={styles.footNum}>{count}</span>
      <span className={styles.footLabel}>{label}</span>
    </button>
  );
}

/* ==========================================================================
   The view
   ========================================================================== */

export function RobotList() {
  const robotsQuery = useRobots();
  const { data: robots } = robotsQuery;
  const { navigate } = useFleetRoute();
  const qc = useQueryClient();

  const [showNew, setShowNew] = useState(false);
  /* The credential a registration just minted, held only until the operator has
     copied it — see the dialog below. */
  const [issued, setIssued] = useState<{ robotId: string; token: EnrollmentToken } | null>(null);
  /* Read once, at mount. The layout is a preference and preferences are not
     state the render loop owns — re-reading storage on every render would make
     a second tab's edit arrive mid-scan. */
  const [prefs] = useState(loadPrefs);
  const [view, setView] = useState<ViewKey>(
    /* A stored `custom` selection with no stored column set is not a view. */
    prefs.view === 'custom' && prefs.custom == null ? 'operator' : prefs.view ?? 'operator',
  );
  const [custom, setCustom] = useState<readonly Slot[] | null>(prefs.custom ?? null);
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [sort, setSort] = useState<SortState>(prefs.sort ?? { key: 'robot', dir: 'asc' });
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [trendKey, setTrendKey] = useState<TrendKey>(prefs.trend ?? 'battery');
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [confirmDrop, setConfirmDrop] = useState(false);
  const [copied, setCopied] = useState(false);
  const popRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  /** The grid's own width, measured. Null until the first layout pass. */
  const [avail, setAvail] = useState<number | null>(null);

  const fleet = useMemo(() => robots ?? [], [robots]);
  const trend = TRENDS.find((t) => t.key === trendKey) ?? TRENDS[0];
  const trendPx = trendColPx(trend);

  /* The layout, back to storage whenever any part of it moves. */
  useEffect(() => {
    savePrefs({ view, custom, trend: trendKey, sort });
  }, [view, custom, trendKey, sort]);

  /* ---- how wide the grid is -------------------------------------------- */
  /* Measured off the table's own scroll container rather than off the window,
     because that is the box the columns actually have to add up to: it is
     inside the panel's border and it is what loses ~15px the moment a
     vertical scrollbar appears. Read once at layout and again on every
     resize; nothing else can change it, so the columns never move for a
     reason the operator did not cause. */
  useLayoutEffect(() => {
    const host = gridRef.current;
    if (host === null) return;
    const read = () => {
      const table = host.querySelector('table');
      const box = table?.parentElement;
      const w = box instanceof HTMLElement ? box.clientWidth : host.clientWidth;
      if (w > 0) setAvail((prev) => (prev !== null && Math.abs(prev - w) < 1 ? prev : w));
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(host);
    return () => {
      ro.disconnect();
    };
  }, []);

  /* ---- which columns are on screen, and how wide ------------------------ */
  /* The TICKED set — what the view asks for, regardless of whether it fits. */
  const slots = useMemo<readonly Slot[]>(() => {
    if (view === 'custom' && custom !== null) return custom;
    return PRESETS[view === 'developer' ? 'developer' : 'operator'];
  }, [view, custom]);

  const tickedSet = useMemo(() => new Set<ColKey>(slots.map((s) => s.key)), [slots]);

  const layout = useMemo(() => resolveLayout(slots, avail, trendPx), [slots, avail, trendPx]);
  const visible = useMemo(() => layout.cols.map((c) => c.key), [layout]);
  const visibleSet = useMemo(() => new Set<ColKey>(visible), [visible]);
  const needsTelemetry = COL_META.some((c) => c.telemetry === true && visibleSet.has(c.key));

  /* ---- fleet-wide telemetry windows ------------------------------------ */
  const ids = useMemo(() => fleet.map((r) => r.robot_id), [fleet]);
  const windows = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['telemetry', id, TREND_SAMPLES] as const,
      queryFn: () => fetchWindow(id),
      enabled: needsTelemetry,
      refetchInterval: 20_000,
      staleTime: 15_000,
    })),
  });
  const windowSig = windows.map((w) => w.dataUpdatedAt).join(',');

  const series = useMemo(() => {
    const now = Date.now();
    const map = new Map<string, Series>();
    ids.forEach((id, i) => {
      const readings = windows[i]?.data;
      if (readings === undefined || readings.length === 0) return;
      /* The API hands them back newest-first; a trace reads oldest → newest. */
      const ordered = [...readings].reverse();
      const latest = ordered[ordered.length - 1] ?? null;
      const first = ordered[0];
      const latestMs = latest === null ? null : new Date(latest.ts).getTime();
      const firstMs = first === undefined ? null : new Date(first.ts).getTime();
      const ageMs = latestMs === null || Number.isNaN(latestMs) ? null : now - latestMs;
      map.set(id, {
        values: ordered.map((t) => trend.read(t)),
        times: ordered.map((t) => t.ts),
        fresh: latest !== null && ageMs !== null && ageMs <= READING_FRESH_MS ? latest : null,
        spanMs: latestMs !== null && firstMs !== null ? latestMs - firstMs : null,
        ageMs,
      });
    });
    return map;
    // windowSig is the structural signature of `windows`: a new results array
    // every render must not rebuild the map (and with it every column).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowSig, ids, trend]);

  /* ---- when each faulted robot entered the mode it is in --------------- */
  /* One wide telemetry request per FAULTED robot, and only per faulted robot.
     The fleet row has no `status_changed_at` to read, so the onset has to be
     found in the robot's own reported `state`; asking for all 28 would be
     ~6MB a minute to date six cells. It refreshes a twelfth as often as the
     fleet list, because a fault that started four hours ago does not start
     again between polls. */
  const timedIds = useMemo(
    () =>
      fleet
        .filter((r) => TIMED_STATUS.has(r.status))
        .map((r) => r.robot_id)
        .sort()
        .slice(0, STATE_SCAN_MAX),
    [fleet],
  );
  const stateRunQueries = useQueries({
    queries: timedIds.map((id) => ({
      queryKey: ['fleet-state-run', id] as const,
      queryFn: () => fetchStateRun(id),
      enabled: visibleSet.has('status'),
      refetchInterval: 60_000,
      staleTime: 55_000,
    })),
  });
  const runSig = stateRunQueries.map((q) => `${q.dataUpdatedAt}:${q.status}`).join(',');
  const stateRuns = useMemo(() => {
    const map = new Map<string, StateRun>();
    timedIds.forEach((id, i) => {
      const d = stateRunQueries[i]?.data;
      if (d != null) map.set(id, d);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runSig, timedIds]);

  /* ---- revision catalogue, for the build column ------------------------ */
  const morphologies = useMemo(
    () => [...new Set(fleet.map((r) => r.morphology))].sort(),
    [fleet],
  );
  const wantsBuild = visibleSet.has('build') || visibleSet.has('board');
  const revQueries = useQueries({
    queries: morphologies.map((m) => ({
      queryKey: ['catalog-versions', m] as const,
      queryFn: () => fetchRevisions(m),
      enabled: wantsBuild,
      staleTime: 300_000,
    })),
  });
  const revSig = revQueries.map((q) => q.dataUpdatedAt).join(',');
  const revisions = useMemo(() => {
    const map = new Map<number, RevisionRow>();
    for (const q of revQueries) for (const v of q.data ?? []) map.set(v.id, v);
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revSig]);

  /* ---- search + facets -------------------------------------------------- */
  const trimmed = query.trim().toLowerCase();

  const searched = useMemo(() => {
    if (trimmed === '') return fleet;
    return fleet.filter((r) =>
      [
        r.robot_id, r.name ?? '', r.model, r.morphology, r.firmware ?? '',
        metaString(r, 'site') ?? '', metaString(r, 'site_label') ?? '',
        metaString(r, 'bay') ?? '', r.enrolled_by ?? '',
        /* so `sim` finds the simulated half of a mixed fleet */
        metaString(r, 'kind') ?? '',
      ].some((f) => f.toLowerCase().includes(trimmed)),
    );
  }, [fleet, trimmed]);

  const now = useMemo(() => Date.now(), [searched]); // eslint-disable-line react-hooks/exhaustive-deps

  const passes = useCallback(
    (r: Robot, except?: FacetKey) =>
      FACET_KEYS.every((k) => {
        if (k === except) return true;
        const set = filters[k];
        if (set.size === 0) return true;
        return facetValues(k, r, now).some((v) => set.has(v));
      }),
    [filters, now],
  );

  const rows = useMemo(() => {
    const kept = searched.filter((r) => passes(r));
    const dir = sort.dir;
    const value = (r: Robot): string | number | null => {
      switch (sort.key) {
        case 'robot': return r.robot_id;
        case 'morphology': return r.morphology;
        case 'model': return r.model;
        case 'board': return (revisions.get(r.morphology_version_id ?? -1)?.meta['board'] as string | undefined) ?? null;
        /* BOTH STATE COLUMNS SORT BY WHAT THEY PRINT — the word FIRST, then
           the duration beside it, longest at the top. Ascending is worst
           first, and inside one word the robot that has been wearing it
           longest is the one nobody has picked up: two faults are not the same
           fault when one is four minutes old and the other eleven hours. The
           two keys are packed into one scalar (rank major, age minor,
           descending) so the comparator stays a subtraction. */
        case 'status': return STATUS_RANK[r.status] * SORT_RANK_STEP - (modeAgeMs(r, stateRuns, now) ?? 0);
        case 'link': return LIVENESS_RANK[r.liveness] * SORT_RANK_STEP - (linkAgeMs(r, now) ?? 0);
        case 'battery': return r.battery_pct;
        case 'trend': {
          /* Sorts by whatever the column PRINTS, which is the only ordering a
             reader can verify against the pixels. With a reservoir on display
             that is the runway, so ascending is the recovery order — the robot
             that empties soonest, first. Elsewhere it is the change, so
             ascending is "falling fastest first".

             A row heading for the CEILING (a robot on a charger) has no
             runway to compare against one heading for the floor, and sorts as
             no-data — the comparator puts that last in both directions, which
             is where a robot that is filling up rather than running out
             belongs in a recovery order.

             Same freshness gate the cell uses: an archive sorts as no data. */
          const s = series.get(r.robot_id);
          if (s === undefined || !isCurrent(s)) return null;
          const ends = windowEnds(s.values);
          if (ends === null) return null;
          if (trend.runway !== true) return ends[1] - ends[0];
          const run = runwayOf(trend, ends[1], windowRate(s.values, s.times));
          if (run === null) return null;
          return run.rate < 0 ? run.ms : null;
        }
        case 'rssi': return rssiOf(series.get(r.robot_id)?.fresh);
        case 'speed': return series.get(r.robot_id)?.fresh?.speed_mps ?? null;
        case 'cpu': return series.get(r.robot_id)?.fresh?.cpu_pct ?? null;
        case 'temp': return series.get(r.robot_id)?.fresh?.temp_c ?? null;
        case 'disk': return series.get(r.robot_id)?.fresh?.disk_used_pct ?? null;
        case 'last_seen': return seenAgeMs(r, now);
        case 'site': return metaString(r, 'site');
        case 'bay': return metaString(r, 'bay');
        case 'position': return r.lat;
        case 'heading': return r.heading_deg;
        case 'agent': return r.firmware;
        case 'build': return revisions.get(r.morphology_version_id ?? -1)?.version ?? null;
        case 'cert': return certDays(r, now);
        case 'enrolled_by': return r.enrolled_by;
        case 'enrolled': return r.enrolled_at;
        default: return r.robot_id;
      }
    };
    return [...kept].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      /* A missing reading sorts last in both directions: "no data" is not
         "smallest", and burying it under a descending sort hides it. */
      if (av === null && bv === null) return a.robot_id.localeCompare(b.robot_id);
      if (av === null) return 1;
      if (bv === null) return -1;
      const d =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv));
      if (d !== 0) return dir === 'asc' ? d : -d;
      return a.robot_id.localeCompare(b.robot_id);
    });
  }, [searched, passes, sort, now, series, revisions, trend, stateRuns]);

  /* ---- facet groups, counted against everything except themselves ------- */
  const facetGroups = useMemo(() => {
    const count = (k: FacetKey, v: string) =>
      searched.filter((r) => passes(r, k) && facetValues(k, r, now).includes(v)).length;

    const sites = [...new Set(fleet.map((r) => metaString(r, 'site') ?? NO_VALUE))].sort();
    const morphs = [...new Set(fleet.map((r) => r.morphology))].sort();

    const siteLabel = (site: string): string | undefined => {
      const owner = fleet.find((r) => (metaString(r, 'site') ?? NO_VALUE) === site);
      return owner === undefined ? undefined : metaString(owner, 'site_label') ?? undefined;
    };

    const build = (
      key: FacetKey,
      items: { value: string; label: string; tone?: Tone | undefined; title?: string | undefined }[],
    ) => {
      const withCounts = items.map((it) => ({ ...it, count: count(key, it.value) }));
      const peak = Math.max(1, ...withCounts.map((i) => i.count));
      return { key, label: FACET_LABEL[key], peak, items: withCounts };
    };

    return [
      build('flag', FLAGS.map((f) => ({ value: f.value, label: f.label, tone: f.tone }))),
      build('status', STATUS_ORDER.map((s) => ({
        value: s, label: STATUS_STATE[s].label, tone: STATUS_STATE[s].tone, title: STATUS_STATE[s].hint,
      }))),
      build('link', LIVENESS_ORDER.map((l) => ({
        value: l, label: LIVENESS_STATE[l].label, tone: LIVENESS_STATE[l].tone, title: LIVENESS_STATE[l].hint,
      }))),
      build('morphology', morphs.map((m) => ({ value: m, label: m }))),
      build('site', sites.map((s) => ({ value: s, label: s, title: siteLabel(s) }))),
    ];
  }, [fleet, searched, passes, now]);

  /* ---- fleet counters for the status bar ------------------------------- */
  const counts = useMemo(() => {
    let live = 0;
    let faults = 0;
    let low = 0;
    let never = 0;
    for (const r of fleet) {
      if (r.liveness === 'live') live += 1;
      if (r.status === 'error') faults += 1;
      if (r.battery_pct != null && r.battery_pct <= BATTERY_WARN) low += 1;
      if (r.liveness === 'provisioning') never += 1;
    }
    return { live, faults, low, never };
  }, [fleet]);

  const clauses = useMemo(
    () => FACET_KEYS.filter((k) => filters[k].size > 0),
    [filters],
  );
  const clauseCount = clauses.length + (trimmed === '' ? 0 : 1);

  /* ---- filter mutation helpers ----------------------------------------- */
  const toggleFacet = useCallback((key: FacetKey, value: string) => {
    setFilters((prev) => {
      const next = new Set(prev[key]);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...prev, [key]: next };
    });
  }, []);

  const clearFacet = useCallback((key: FacetKey) => {
    setFilters((prev) => ({ ...prev, [key]: new Set<string>() }));
  }, []);

  const clearAll = useCallback(() => {
    setFilters(NO_FILTERS);
    setQuery('');
  }, []);

  /* ---- column visibility ------------------------------------------------ */
  /* Ticking works on what the VIEW asks for, never on what currently fits: a
     column the viewport has shed is still ticked, and unticking it has to
     take it out of the view rather than silently do nothing. */
  const toggleColumn = useCallback(
    (key: ColKey) => {
      const next = [...slots];
      const at = next.findIndex((s) => s.key === key);
      if (at >= 0) {
        if (next.length <= 2) return; // never leave the grid with one column
        next.splice(at, 1);
      } else {
        /* Inserted where the catalogue says it belongs AMONG THE COLUMNS
           ALREADY UP, rather than re-deriving the whole order from the
           catalogue. A preset orders its columns deliberately — Operator ends
           on `Last seen` at the table's trailing edge — and re-sorting on
           every toggle would make ticking one box shuffle four others. */
        const rank = COL_ORDER.indexOf(key);
        const before = next.findIndex((s) => COL_ORDER.indexOf(s.key) > rank);
        const shed = CUSTOM_SHED[key];
        const slot: Slot = shed === undefined ? { key } : { key, shed };
        next.splice(before < 0 ? next.length : before, 0, slot);
      }
      setCustom(next);
      setView('custom');
    },
    [slots],
  );

  /* ---- menus ------------------------------------------------------------ */
  const openMenu = useCallback(
    (kind: MenuKind, e: ReactMouseEvent<HTMLButtonElement>, align: 'left' | 'right') => {
      const anchor = e.currentTarget;
      const r = anchor.getBoundingClientRect();
      const width = MENU_WIDTH[kind];
      setMenu((prev) =>
        prev !== null && prev.kind === kind
          ? null
          : {
            kind,
            width,
            left: clamp(align === 'right' ? r.right - width : r.left, 6, window.innerWidth - width - 6),
            top: r.bottom + 3,
            anchor,
          },
      );
    },
    [],
  );

  useEffect(() => {
    if (menu === null) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) === true) return;
      if (menu.anchor.contains(t)) return;
      setMenu(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setMenu(null);
      menu.anchor.focus();
    };
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  /* `/` focuses the filter box — the console's convention. The primitives are
     not forwardRef, so the field is addressed by id rather than by ref. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t !== null && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const box = document.getElementById('fleet-search');
      if (box === null) return;
      e.preventDefault();
      box.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  /* ---- bulk actions ----------------------------------------------------- */
  const patchMany = useMutation({
    mutationFn: async ({ keys, status }: { keys: string[]; status: RobotStatus }) => {
      await Promise.all(
        keys.map(async (id) => {
          const r = await apiFetch(`/api/robots/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ status }),
          });
          if (!r.ok) throw new Error(`${id}: ${r.status}`);
        }),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['robots'] });
      /* A hold or a release IS a mode transition, so the cached onset scan is
         now dated from the wrong mode. Until it refetches the Status cell
         prints the word with no duration — which is correct, and briefly less
         informative than it needs to be, so it is refetched immediately
         rather than at the next 60s tick. */
      void qc.invalidateQueries({ queryKey: ['fleet-state-run'] });
    },
  });

  const dropMany = useMutation({
    mutationFn: async (keys: string[]) => {
      await Promise.all(
        keys.map(async (id) => {
          const r = await apiFetch(`/api/robots/${encodeURIComponent(id)}`, { method: 'DELETE' });
          if (!r.ok && r.status !== 204) throw new Error(`${id}: ${r.status}`);
        }),
      );
    },
    onSuccess: () => {
      setSelected(new Set());
      setConfirmDrop(false);
      void qc.invalidateQueries({ queryKey: ['robots'] });
    },
  });

  const picked = useMemo(() => fleet.filter((r) => selected.has(r.robot_id)), [fleet, selected]);
  const holdable = picked.filter((r) => r.status !== 'maintenance').map((r) => r.robot_id);
  const releasable = picked.filter((r) => r.status === 'maintenance').map((r) => r.robot_id);

  const copyIds = useCallback(() => {
    const text = picked.map((r) => r.robot_id).join('\n');
    void navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(true);
        window.setTimeout(() => {
          setCopied(false);
        }, 1400);
      },
      () => {
        setCopied(false);
      },
    );
  }, [picked]);

  useEffect(() => {
    if (selected.size === 0) setConfirmDrop(false);
  }, [selected]);

  /* ---- the column set, resolved --------------------------------------- */
  /* The window the header quotes is the median of the windows actually being
     DRAWN. An archive that the column now voids must not vote on the label
     over the traces beside it — a robot eleven hours dark once dragged the
     header's own honesty with it. */
  const trendSpan = useMemo(() => {
    const spans = [...series.values()]
      .filter(isCurrent)
      .map((s) => s.spanMs)
      .filter((v): v is number => v != null);
    if (spans.length === 0) return null;
    spans.sort((a, b) => a - b);
    return spans[Math.floor(spans.length / 2)] ?? null;
  }, [series]);

  const columns = useMemo<readonly Column<Robot>[]>(() => {
    const reading = (r: Robot) => series.get(r.robot_id) ?? NO_SERIES;
    const revOf = (r: Robot) =>
      r.morphology_version_id === null ? undefined : revisions.get(r.morphology_version_id);

    /** When this robot entered the mode it is in, if the console can see it.
        Null while the scan is in flight, past the fan-out ceiling, or when the
        record's mode and the robot's newest reported mode disagree — three
        different reasons to print nothing, and none of them a reason to print
        a guess. */
    const modeClock = (r: Robot): StateClock | null => {
      const run = stateRuns.get(r.robot_id);
      if (run === undefined) return null;
      if (run.state !== r.status) return null;
      return {
        since: run.sinceIso,
        floor: run.atLeast,
        lead: `${STATUS_STATE[r.status].label} for`,
        stampLead: 'since',
        note:
          `Dated from the robot's own reported mode — ${run.samples} consecutive readings\n`
          + `Last confirmed by telemetry ${utc(run.confirmedIso)}`
          + (run.atLeast
            ? `\nThe ${STATE_SCAN_LIMIT}-reading window never reached the transition, so this is a lower bound`
            : ''),
      };
    };

    const all: Record<ColKey, Column<Robot>> = {
      robot: {
        key: 'robot',
        header: 'Robot',
        kind: 'identity',
        sortable: true,
        /* THE ONE ELASTIC COLUMN. Every other column below is pinned to a px
           width that the rows cannot influence; this one is left unpinned and
           marked `flex`, so DataTable's resolver hands it the difference
           between the pinned columns and the panel. That is what makes the
           widths sum to the wrapper — no dead band for the row hover to paint
           — while keeping the grid a function of the view and the viewport.
           `data-stretch` on the cell is the primitive's own opt-out from ink
           measurement: without it the column would be sized to the longest
           robot name currently on screen, which is the defect this whole
           layout exists to remove. */
        flex: true,
        cell: (r) => {
          /* SIM-VS-REAL RIDES THE NAME.

             THE BAR §6 lists simulated-vs-real among the four provenance
             facts that must be on screen, and a fleet that mixes hardware
             with Isaac Sim instances cannot afford the reader guessing: an
             8% battery on a simulated rover is not a callout, and a fault on
             one is not an incident. `meta.kind` carries it.

             Marked only where it DEVIATES. Printing `hardware` on
             twenty-eight rows would be a column of one repeated word, which
             is the ink an exception needs in order to be seen. This is
             Anduril's `Simulated` marker at row scale — and deliberately not
             a chip, because a badge that repeats per row is tell #2. */
          const kind = metaString(r, 'kind');
          return (
            <span className={styles.idCell} data-stretch="true">
              {/* THE ID IS A LINK, AND IT IS THE ONLY ONE ON THIS ROW.

                  Every destination in this console was a `<button>`: the rail,
                  the tabs, the crumb, the rows. The only `<a href>` in any of
                  the thirteen routes was the skip link — for an internal tool
                  whose working day is people sending each other robot ids, so
                  no middle-click, no ⌘-click, no "copy link address", no
                  hover preview of where a control goes. The row's own click
                  still opens the robot; this makes the same destination one
                  the browser understands. */}
              <RobotLink id={r.robot_id} className={styles.rid} />
              {r.name !== null && <span className={styles.rname}>{r.name}</span>}
              {kind !== null && kind !== 'hardware' && (
                <span className={styles.rkind} title={`Not physical hardware — meta.kind ${kind}`}>
                  {kind}
                </span>
              )}
            </span>
          );
        },
      },
      morphology: {
        key: 'morphology', header: 'Morphology', kind: 'enum', mono: true, sortable: true,
        cell: (r) => r.morphology,
      },
      model: {
        key: 'model', header: 'Model', kind: 'id', mono: true, sortable: true,
        cell: (r) => r.model,
      },
      board: {
        key: 'board', header: 'Board', kind: 'id', mono: true, sortable: true,
        cell: (r) => {
          const b = revOf(r)?.meta['board'];
          return typeof b === 'string' ? b : NO_VALUE;
        },
      },
      /* Both state columns run their tone through `matrixTone`, the same
         projection the fleet matrix uses. A page that names ONE robot may
         paint a green `Live`; twenty-eight green `Live` cells are a stripe
         with no break in it, and the two rows an operator has to find stop
         being findable. The word is unchanged, the dot's fill/hollow shape is
         unchanged — only the saturation the healthy majority was spending. */
      status: {
        key: 'status',
        /* THE HEADER NAMES BOTH QUANTITIES.

           The cell holds a word and a clock, so the header holds a noun and a
           preposition: `Status since`. Four dim characters, in the same micro
           step the Trend header uses for its own qualifier, so a reader never
           has to hover a column to learn what the second number in it is —
           THE BAR's fast fail is "a tooltip that explains what a column means
           instead of a header that says it". */
        header: (
          <>
            <span className={styles.headIndent} aria-hidden="true" />
            Status
            <span className={styles.headQual}>since</span>
          </>
        ),
        kind: 'status',
        sortable: true,
        /* THE WORD, AND HOW LONG IT HAS BEEN THE WORD.

           The mode comes off the fleet record. The DATE comes off the robot's
           own telemetry, because the record carries no transition timestamp:
           `updated_at` moves every time a reading lands, so it says when we
           last heard from the robot, not when it faulted. The scan walks back
           through the reported `state` until it changes, which is a real
           observation with a real timestamp behind it and is auditable in the
           cell's title — sample count, first reading in the mode, and when the
           mode was last confirmed.

           It is shown only where the record and the newest reading AGREE. A
           robot the record calls `error` whose last reading said `charging` is
           a robot whose transition the console did not see, and inventing a
           date for it would be worse than leaving the cell as it was. */
        cell: (r) => {
          const st = STATUS_STATE[r.status];
          const tone = matrixTone(st.tone);
          const loud = tone !== 'nominal';
          return (
            <StateCell
              tone={tone}
              label={st.label}
              loud={loud}
              hollow={st.hollow}
              hint={st.hint}
              clock={loud ? modeClock(r) : null}
            />
          );
        },
      },
      link: {
        key: 'link',
        header: (
          <>
            <span className={styles.headIndent} aria-hidden="true" />
            Link
            <span className={styles.headQual}>since</span>
          </>
        ),
        kind: 'status',
        sortable: true,
        /* THE WORD IS A READING OF THE NUMBER BESIDE IT.

           Liveness is derived server-side from telemetry recency, so `Stale`
           and `10m23s` are not two facts — they are the verdict and the
           measurement it was reached from, and printing them together is what
           turns a five-value enum into an instrument. It also answers the
           freshness question in the cell that owns it rather than in a
           `Last seen` column a thousand pixels away, which is why that column
           is no longer in either preset.

           `Provisioning` is the one row where the clock is a different clock:
           a robot that has never phoned home has no `last_seen`, so the
           duration is how long it has been on the books waiting to. The title
           says which, on every row. */
        cell: (r) => {
          const lv = LIVENESS_STATE[r.liveness];
          const tone = matrixTone(lv.tone);
          const since = linkSince(r);
          /* `Backfilling` is the one liveness word that names a job in
             progress rather than a condition, so it carries the job's own
             timestamp: the record knows when the last backfill landed, and
             without it the word is a claim with no clock behind it. */
          const backfill = r.liveness === 'backfilling' ? r.last_backfill_at : null;
          return (
            <StateCell
              tone={tone}
              label={lv.label}
              loud={tone !== 'nominal'}
              /* `live` resolves through its OWN palette rather than the
                 projected one — that indirection is why tokens.css keeps a
                 `live` tone whose hex happens to match `good`. The projection
                 to steel is right for the fleet MATRIX, where 168 cells are on
                 screen and green would drown the four that are not; it is wrong
                 for one column of one register, where the reader is asking
                 which rows are up. */
              {...(r.liveness === 'live' ? { markTone: 'live' as Tone } : {})}
              hollow={lv.hollow}
              hint={lv.hint}
              clock={
                since === null
                  ? null
                  : {
                    since: since.iso,
                    ago: true,
                    lead: since.what === 'registered' ? 'Registered' : 'Last reported',
                    note:
                      (since.what === 'registered'
                        ? 'Has never reported — this is time on the books, not link age'
                        : 'Time since the newest live reading — the measurement this state is derived from')
                      + (backfill === null ? '' : `\nLast backfill batch landed ${utc(backfill)}`),
                  }
              }
            />
          );
        },
      },
      /* THE LINK WORD AND THE LINK MEASUREMENT ARE TWO DIFFERENT FACTS.
         `Link` says whether the cloud is hearing this robot; RSSI says how
         hard the radio is working to make that true, which is the number that
         moves BEFORE the word does. THE BAR tell #27: one connection light
         standing in for every channel is a defect. It rides the telemetry
         stream, so it obeys the same freshness law as speed and CPU — a
         hatched track and an em dash the moment the reading stops being now.
         Bounded −95…−35 dBm because that is the span a 2.4/5GHz link lives
         in, with the console's own published limits printed on the track. */
      rssi: {
        key: 'rssi', header: 'RSSI', kind: 'meter', sortable: true,
        cell: (r) => (
          <MeterCell
            value={rssiOf(reading(r).fresh)}
            min={RSSI_MIN}
            max={RSSI_MAX}
            unit="dBm"
            warn={RSSI_WARN}
            bad={RSSI_BAD}
          />
        ),
      },
      battery: {
        key: 'battery', header: 'Battery', kind: 'meter', sortable: true,
        /* The record's last reported charge — the ONE live number in this row
           that survives the robot going quiet, because it rides the fleet
           record rather than the telemetry stream. So it does not vanish, it
           DIMS: a low battery on an unreachable robot is still a low battery,
           and it has simply stopped being a claim about now. How far from now
           is answered by the Link cell three columns to its left, which prints
           the silence age on every row and is never shed. */
        cell: (r) => (
          <RecordCell robot={r}>
            <MeterCell value={r.battery_pct} warn={BATTERY_WARN} bad={BATTERY_BAD} />
          </RecordCell>
        ),
      },
      trend: {
        key: 'trend',
        /* The header states all three facts a trace needs to be readable — the
           QUANTITY, the WINDOW it covers and the SCALE its height is drawn
           against — and it is also the control that changes them. Both halves
           of that matter. A column headed only `Trend` makes the reader hunt
           the far side of the toolbar to learn what the line is a line of, and
           a selector 700px from the thing it selects is a control filed under
           the wrong object.

           THE NOUN SORTS, THE QUALIFIER PICKS. Two sibling controls in one
           header, because the column now has two dimensions and they are
           different questions. `Trend ↑` orders the fleet by the readout —
           with Battery on display that is the recovery order, soonest empty
           first, which nothing else in this table answers (sorting Battery
           orders by LEVEL, and a robot at 40% draining fast comes in before
           one at 30% sitting on a charger). `Battery 1h14m ⌄` changes the
           quantity. Neither is nested in the other; DataTable's own sort
           button is not used because a button inside a button is not a
           control, it is a bug. */
        header: (
          <span className={styles.trendHeadGroup}>
            <button
              type="button"
              className={styles.trendSort}
              title={
                trend.runway === true
                  ? 'Sort by runway — the robot that empties soonest first'
                  : `Sort by the change in ${trend.label.toLowerCase()} across the window`
              }
              onClick={() => {
                setSort((prev) => ({
                  key: 'trend',
                  dir: prev.key === 'trend' && prev.dir === 'asc' ? 'desc' : 'asc',
                }));
              }}
            >
              Trend
              <span className={styles.trendSortGlyph} data-on={sort.key === 'trend'}>
                <Icon
                  name={sort.key === 'trend' && sort.dir === 'desc' ? 'arrow-down' : 'arrow-up'}
                  size={12}
                />
              </span>
            </button>
            <button
              type="button"
              className={styles.trendHead}
              aria-haspopup="dialog"
              aria-expanded={menu?.kind === 'trend'}
              /* The column's whole contract in one sentence, including the two
                 rules a reader cannot infer from the pixels: which readout
                 this quantity carries, and what decides whether a row gets a
                 trace at all. A hatched track is a policy, and a policy the
                 reader cannot find is a bug. */
              title={
                `${trend.label} over the last ${trendSpan === null ? 'window' : duration(trendSpan)}, ` +
                `${TREND_SAMPLES} samples per robot · scale ${trendScale(trend)}\n` +
                `Readout: ${readoutName(trend)}\n` +
                `A robot with no reading inside ${duration(READING_FRESH_MS)} draws a hatched track, not a curve\n` +
                `Click to change the quantity`
              }
              aria-label={`Trend quantity: ${trend.label}, scale ${trendScale(trend)}, readout ${readoutName(trend)}`}
              onClick={(e) => { openMenu('trend', e, 'left'); }}
            >
              <span className={styles.headWindow}>
                {trend.label}
                {trendSpan !== null && <span className={styles.headSpan}>{compactSpan(trendSpan)}</span>}
                {/* The normalisation, but only where it is not already implied.
                    A `%` column is 0–100 by definition; a fitted box has to say
                    so, because its height is relative and nothing else on the
                    row admits it. */}
                {trend.max === undefined && <span className={styles.headScale}>fit</span>}
              </span>
              <Icon name="chevron-down" size={11} />
            </button>
          </span>
        ),
        align: 'right',
        cell: (r) => {
          const s = reading(r);
          /* THE ONE FRESHNESS LAW, APPLIED TO THE TRACE.

             A sparkline is a window ending NOW. Where the newest sample is
             older than the reading limit, there is no trend to draw — only an
             archive — so the box draws the SAME hatched no-data track the
             Speed / CPU / Temp / Disk meters in this row are already drawing,
             and the change reads `—`. Dimming it was not enough: a dim curve
             is still a curve with a direction and a delta, and it still
             claimed both for a robot that has not spoken in seven hours.

             The same treatment covers the two other ways a trace can have
             nothing to say — a robot that has never reported at all, and a
             window that carries no reading of THIS quantity — because all
             three are the same fact and a table must not have three
             renderings of it. */
          const ends = isCurrent(s) ? windowEnds(s.values) : null;
          if (ends === null) {
            const why =
              s.ageMs === null
                ? `No telemetry recorded for ${r.robot_id}`
                : s.ageMs > READING_FRESH_MS
                  ? `No current ${trend.label.toLowerCase()} trend — newest reading is ${duration(s.ageMs)} old, past the ${duration(READING_FRESH_MS)} limit`
                  : `No ${trend.label.toLowerCase()} readings in this window`;
            return (
              <span className={styles.trendCell} title={why}>
                <span
                  className={styles.trendVoid}
                  role="img"
                  aria-label={why}
                  style={{ width: `${TREND_W}px`, height: `${TREND_H}px` }}
                />
                <span
                  className={styles.trendDelta}
                  data-void="true"
                  style={{ '--delta-ch': `${readoutCh(trend)}ch` } as CSSProperties}
                >
                  {NO_VALUE}
                </span>
              </span>
            );
          }
          const span = s.spanMs === null ? null : duration(s.spanMs);
          const fmt = (v: number) => `${v.toFixed(trend.digits)}${trend.unit}`;
          const samples = s.values.reduce<number>((a, v) => a + (v == null ? 0 : 1), 0);
          const rate = windowRate(s.values, s.times);
          const run = runwayOf(trend, ends[1], rate);
          /* Every readout in this column carries the same four facts in its
             title — both ends, the window, the sample count and the measured
             rate — so a forecast and a change are audited the same way. The
             projection then adds the one sentence that makes it a projection
             rather than a promise. */
          const basis =
            `${fmt(ends[0])} → ${fmt(ends[1])}${span === null ? '' : ` over ${span}`}`
            + ` · ${samples} readings`
            + (rate === null
              ? ''
              : ` · ${signed(rate, Math.abs(rate) < 1 ? 2 : 1)} ${trend.deltaUnit}/h`);
          return (
            <span
              className={styles.trendCell}
              /* Where a hard limit is drawn, the box's own floor rule is
                 suppressed — see the note in the stylesheet. */
              data-limited={trend.bad !== undefined || trend.warn !== undefined}
              style={{ '--delta-ch': `${readoutCh(trend)}ch` } as CSSProperties}
            >
              <Sparkline
                values={s.values}
                times={s.times}
                width={TREND_W}
                height={TREND_H}
                head
                unit={trend.unit}
                digits={trend.digits}
                label={`${r.robot_id} ${trend.label.toLowerCase()}`}
                {...(trend.warn === undefined ? {} : { warn: trend.warn })}
                {...(trend.bad === undefined ? {} : { bad: trend.bad })}
                {...(trend.invert === undefined ? {} : { invert: trend.invert })}
                {...(trend.min === undefined ? {} : { min: trend.min })}
                {...(trend.max === undefined ? {} : { max: trend.max })}
              />
              {/* THE DEADLINE, WHERE THERE IS ONE.

                  A reservoir's trend has a bottom, and the number an operator
                  wants off this column is when it gets there — which is the
                  level over the rate, and so is different on every row even
                  when the whole fleet discharges at one rate. `9h45m →0%` on
                  one row and `58m →0%` on another is the recovery order, in
                  the column that already draws the curve behind it.

                  Where there is no deadline — a load, a temperature, a speed,
                  or a reservoir too flat to project — the cell prints what the
                  window actually DID, in the unit a CHANGE in this quantity is
                  measured in: `−10 pp`, not `−10%`, because ten percentage
                  points beside a meter reading `80%` would otherwise be heard
                  as ten percent of the charge.

                  Grey, always. A battery discharging is a battery working; the
                  alarm colours belong to the trace and to its limit rule. */}
              {run === null ? (
                <span className={styles.trendDelta} title={basis}>
                  {signed(ends[1] - ends[0], trend.digits)}
                  <Unit unit={trend.deltaUnit} className={styles.trendUnit} />
                </span>
              ) : (
                <span
                  className={styles.trendDelta}
                  title={
                    `${basis}\n`
                    + `Reaches ${run.target}${trend.unit} in ${duration(run.ms)} `
                    + `(${utc(new Date(Date.now() + run.ms).toISOString())}) `
                    + `if it holds this rate — a straight-line projection, not a guarantee`
                  }
                >
                  {runwayText(run.ms)}
                  {/* The bound it is heading for, as a dim suffix inside the
                      same cell, so the duration is never a bare number and the
                      digits still align on their own edge. */}
                  <span className={styles.runwayTo}>
                    →{run.target}
                    {trend.unit}
                  </span>
                </span>
              )}
            </span>
          );
        },
      },
      speed: {
        key: 'speed', header: 'Speed', kind: 'num', unit: 'm/s', mono: true, sortable: true,
        cell: (r) => num(reading(r).fresh?.speed_mps ?? null, 2),
      },
      /* CPU, Temp and Disk are the same shape as Battery — a bounded reading
         judged against a published limit — so they are drawn by the same
         primitive and obey the same law: the track publishes the hard limit
         as a 1px tick, the fill and the readout recolour together the moment
         it is crossed, and a reading we do not have is a hatched track and an
         em dash, never a zero and never a green bar. Four aligned tracks down
         the row read as texture (`inorbit-fleet-status-matrix.png`): the eye
         finds the break, not the value. */
      cpu: {
        key: 'cpu', header: 'CPU', kind: 'meter', sortable: true,
        cell: (r) => (
          <MeterCell
            value={reading(r).fresh?.cpu_pct ?? null}
            warn={CPU_WARN}
            bad={CPU_BAD}
            invert
          />
        ),
      },
      temp: {
        /* The shed order across the whole Live group is a priority list, not
           an alphabet: bay goes first, then disk, then temperature, then
           speed, so a 1280px viewport still carries id / status / link /
           battery / trend / CPU / site / last seen and a 1600px one carries
           everything but the bay. Raw coordinates step aside for all of them
           — the detail page has a map, this table does not.

           BAY IS SHED BEFORE DISK. A bay is a parking slot that changes once
           a week; a disk filling up is the fault that silently costs the
           session the robot was sent out to record. Ordering them the other
           way round meant the first thing a 1600px viewport gave up was
           twenty-eight live readings, to keep twenty-eight strings that read
           `C3`. */
        key: 'temp', header: 'Temp', kind: 'meter', sortable: true,
        cell: (r) => (
          <MeterCell
            value={reading(r).fresh?.temp_c ?? null}
            min={0}
            max={TEMP_MAX}
            unit="°C"
            warn={TEMP_WARN}
            bad={TEMP_BAD}
            invert
          />
        ),
      },
      disk: {
        key: 'disk', header: 'Disk', kind: 'meter', sortable: true,
        cell: (r) => (
          <MeterCell
            value={reading(r).fresh?.disk_used_pct ?? null}
            warn={DISK_WARN}
            bad={DISK_BAD}
            invert
          />
        ),
      },
      last_seen: {
        key: 'last_seen', header: 'Last seen', kind: 'age', align: 'right', sortable: true,
        cell: (r) => <Timestamp value={r.last_seen} staleAfter={SEEN_WARN_MS / 1000} deadAfter={SEEN_BAD_MS / 1000} />,
      },
      site: {
        key: 'site', header: 'Site', kind: 'id', mono: true, sortable: true,
        cell: (r) => {
          const s = metaString(r, 'site');
          if (s === null) return NO_VALUE;
          return <span title={metaString(r, 'site_label') ?? undefined}>{s}</span>;
        },
      },
      bay: {
        key: 'bay', header: 'Bay', kind: 'enum', mono: true, sortable: true,
        cell: (r) => metaString(r, 'bay') ?? NO_VALUE,
      },
      /* Position and heading ride the record too, so they obey the record's
         law: a seven-hour-old fix dims rather than sitting there looking like
         where the robot is. The age is not repeated here — see RecordCell. */
      position: {
        key: 'position', header: 'Position', kind: 'id', mono: true, sortable: true,
        cell: (r) => <RecordCell robot={r}>{latlon(r.lat, r.lon)}</RecordCell>,
      },
      heading: {
        key: 'heading', header: 'Hdg', kind: 'num', unit: '°', mono: true, sortable: true,
        cell: (r) => <RecordCell robot={r}>{num(r.heading_deg, 0)}</RecordCell>,
      },
      agent: {
        key: 'agent', header: 'Agent', kind: 'id', mono: true, sortable: true,
        cell: (r) => r.firmware ?? NO_VALUE,
      },
      build: {
        key: 'build',
        header: 'Build',
        kind: 'id',
        sortable: true,
        cell: (r) => {
          const rev = revOf(r);
          if (rev === undefined) return <span className={styles.buildNone}>{NO_VALUE}</span>;
          const sha = rev.script_sha256;
          return (
            <span
              className={styles.build}
              data-provisional={!rev.complete}
              title={
                rev.complete
                  ? `${rev.version}${rev.notes === null ? '' : ` — ${rev.notes}`}${sha === null ? '\nNo bringup script pinned' : `\nbringup.sh sha256 ${sha}`}`
                  : `${rev.version} — provisional pin, no container manifest`
              }
            >
              <span className={styles.buildRev}>{rev.version}</span>
              <span className={styles.buildSha}>{sha === null ? NO_VALUE : sha.slice(0, 6)}</span>
            </span>
          );
        },
      },
      cert: {
        key: 'cert', header: 'Cert expires', kind: 'age', align: 'right', sortable: true,
        cell: (r) => (
          <Timestamp
            value={r.cert_not_after}
            warnWithin={CERT_WARN_DAYS * 24 * 3600}
            badWithin={CERT_BAD_DAYS * 24 * 3600}
          />
        ),
      },
      enrolled_by: {
        key: 'enrolled_by', header: 'Enrolled by', kind: 'id', mono: true, dim: true, sortable: true,
        cell: (r) => r.enrolled_by ?? NO_VALUE,
      },
      /* Relative, like `Last seen` and `Cert expires` beside it, with the
         absolute Zulu stamp in the title. Three time columns in one table
         reading three different ways is three vocabularies to hold; the
         answer an operator wants off an enrolment date is "how long has this
         robot been on the books", not the minute it happened. */
      enrolled: {
        key: 'enrolled', header: 'Enrolled', kind: 'age', align: 'right', sortable: true,
        cell: (r) => <Timestamp value={r.enrolled_at} />,
      },
    };

    /* The pinned width goes on last, from the resolved layout, so the geometry
       lives in ONE place — `resolveLayout` — rather than being half a table of
       constants and half a set of props scattered through 400 lines. */
    return layout.cols.map(({ key, width }) =>
      width === null ? all[key] : { ...all[key], width: `${width}px` },
    );
    // `menu?.kind` is in the deps because the trend header carries its own
    // aria-expanded; the column objects are cheap and their geometry key is
    // unchanged, so rebuilding them does not re-measure the table.
  }, [layout, series, stateRuns, revisions, trend, trendSpan, openMenu, menu?.kind, sort]);

  /* ---- render ----------------------------------------------------------- */
  const matched = rows.length;
  const total = fleet.length;
  const filtering = clauseCount > 0;

  /* ONE READ, ONE WORD (components/states). The table used to answer a dead
     API with `Failed to load the fleet. The API did not answer.` over 630px of
     bare ground — its own sentence, its own colour, and its own idea of what a
     failed read looks like, none of which matched the five other surfaces. Now
     the register keeps its columns in every state and the fault is one line
     where the first row would be. */
  const read = useRead(robotsQuery, {
    path: '/api/robots',
    everyMs: 5_000,
    subject: 'the fleet register',
    empty: rows.length === 0,
  });

  /* The instrument IS the column spec, so nothing moves when the rows land. */
  const skeletonColumns = useMemo<readonly SkeletonColumn[]>(
    () => columns.map((c) => ({
      ...(typeof c.header === 'string' ? { label: c.header } : {}),
      ...(c.width === undefined ? {} : { width: c.width }),
      ...(c.align === 'right' ? { align: 'right' as const } : {}),
    })),
    [columns],
  );

  return (
    <SurfaceShell
      eyebrow="Fleet"
      title="Robots"
      subtitle={
        <span className={styles.headCount}>
          {/* `0 registered` was the page title claiming an empty fleet on
             every cold start and for the whole of every outage. The roster is
             a census and a census needs a read. */}
          <span className={styles.headNum}>
            <Figure of={read} value={filtering ? matched : total} />
          </span>
          {filtering && (
            <>
              <span className={styles.headOf}>of</span>
              <span className={styles.headNum}><Figure of={read} value={total} /></span>
            </>
          )}
          <span className={styles.headWord}>registered</span>
        </span>
      }
      actions={
        <Button variant="primary" icon="plus" onClick={() => { setShowNew(true); }}>
          New robot
        </Button>
      }
      pad="none"
    >
      <div className={styles.stack}>
        <Toolbar label="Fleet list controls">
          <SegmentedControl<ViewKey>
            label="Saved view"
            value={view}
            onChange={(v) => {
              if (v === 'custom' && custom === null) return;
              setView(v);
            }}
            items={[
              { value: 'operator', label: 'Operator' },
              { value: 'developer', label: 'Developer' },
              { value: 'custom', label: 'Custom', disabled: custom === null },
            ]}
          />

          <ToolbarDivider />

          <span className={styles.search}>
            <SearchInput
              id="fleet-search"
              aria-label="Search robots"
              placeholder="Search id, site, agent…"
              value={query}
              onChange={(e) => { setQuery(e.currentTarget.value); }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setQuery('');
              }}
            />
            <span className={styles.slash} aria-hidden="true">/</span>
          </span>

          <Button
            size="sm"
            variant="ghost"
            icon="filter"
            iconEnd="chevron-down"
            active={menu?.kind === 'filters' || clauseCount > 0}
            aria-expanded={menu?.kind === 'filters'}
            aria-haspopup="dialog"
            {...(clauseCount > 0 ? { badge: clauseCount } : {})}
            onClick={(e) => { openMenu('filters', e, 'left'); }}
          >
            Filters
          </Button>

          <ToolbarSpacer />

          {/* The count is what is ON SCREEN, not what the view asked for. It
              said `12` while eleven rendered at 1600 and eight at 1280, which
              is a control reporting a number the operator cannot find on the
              surface it labels. Where the two differ the second number names
              the shortfall, and the menu marks which columns it is. */}
          {layout.dropped.size > 0 && (
            <span className={styles.shedNote}>{layout.dropped.size} too narrow</span>
          )}

          <Button
            size="sm"
            variant="ghost"
            icon="columns"
            iconEnd="chevron-down"
            active={menu?.kind === 'columns'}
            aria-expanded={menu?.kind === 'columns'}
            aria-haspopup="dialog"
            badge={visible.length}
            title={
              layout.dropped.size === 0
                ? `${visible.length} columns on screen`
                : `${visible.length} of ${slots.length} columns on screen — ${[...layout.dropped]
                  .map((k) => COL_META.find((c) => c.key === k)?.name ?? k)
                  .join(', ')} ${layout.dropped.size === 1 ? 'does' : 'do'} not fit at this window width`
            }
            onClick={(e) => { openMenu('columns', e, 'right'); }}
          >
            Columns
          </Button>
        </Toolbar>

        {filtering && (
          <div className={styles.chips}>
            <ToolbarLabel>Filter</ToolbarLabel>
            {trimmed !== '' && (
              <Chip icon="search" mono onDismiss={() => { setQuery(''); }} dismissLabel="Clear search">
                {query.trim()}
              </Chip>
            )}
            {clauses.map((k) => (
              <Chip
                key={k}
                onDismiss={() => { clearFacet(k); }}
                dismissLabel={`Clear ${FACET_LABEL[k]} filter`}
              >
                <span className={styles.chipKey}>{FACET_LABEL[k]}</span>
                <span className={styles.chipVals}>
                  {[...filters[k]].map((v) => clauseWord(k, v)).join(', ')}
                </span>
              </Chip>
            ))}
            <span className={styles.chipSpacer} />
            <Button size="sm" variant="ghost" onClick={clearAll}>
              Clear all
            </Button>
          </div>
        )}

        <div className={styles.fill} ref={gridRef}>
        <Panel className={styles.gridPanel} pad="none" loading={read.fetching} scroll={false}>
          <ReadBody
            read={read}
            skeleton={<SkeletonTable columns={skeletonColumns} rows={26} edge what="robots" />}
            empty={
              filtering
                ? (
                  <Blank
                    kind="filtered"
                    noun="robots"
                    detail={`${total} registered · 0 shown`}
                    action={{ label: 'Clear all', onSelect: clearAll }}
                  />
                )
                : <Blank noun="robots" where="registered" />
            }
          >
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(r) => r.robot_id}
            rowTone={rowEdgeTone}
            toneScale="literal"
            rowAlarm={(r) => r.status === 'error'}
            onRowClick={(r) => { navigate('detail', { robotId: r.robot_id }); }}
            sort={sort}
            onSortChange={setSort}
            selectable
            selected={selected}
            onSelectedChange={setSelected}
            actions={
              <span className={styles.bulk}>
                <Button size="sm" variant="ghost" icon="copy" onClick={copyIds}>
                  {copied ? 'Copied' : 'Copy ids'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  icon="lock"
                  {...(holdable.length > 0 ? { badge: holdable.length } : {})}
                  disabled={holdable.length === 0 || patchMany.isPending}
                  onClick={() => { patchMany.mutate({ keys: holdable, status: 'maintenance' }); }}
                >
                  Hold
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  icon="unlock"
                  {...(releasable.length > 0 ? { badge: releasable.length } : {})}
                  disabled={releasable.length === 0 || patchMany.isPending}
                  onClick={() => { patchMany.mutate({ keys: releasable, status: 'idle' }); }}
                >
                  Return to service
                </Button>
                <span className={styles.bulkSpacer} />
                {patchMany.isError && (
                  <span className={styles.bulkError}>{String(patchMany.error)}</span>
                )}
                {confirmDrop ? (
                  <>
                    <span className={styles.bulkConfirm}>
                      Deregister {selected.size}? Removed from the fleet — re-enrolment required to return.
                    </span>
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={dropMany.isPending}
                      onClick={() => { dropMany.mutate([...selected]); }}
                    >
                      Confirm
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setConfirmDrop(false); }}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="danger"
                    icon="trash"
                    badge={selected.size}
                    onClick={() => { setConfirmDrop(true); }}
                  >
                    Deregister
                  </Button>
                )}
              </span>
            }
          />
          </ReadBody>
        </Panel>
        </div>

        <div className={styles.foot}>
          <span className={styles.footItem}>
            <span className={styles.footNum}><Figure of={read} value={matched} /></span>
            {filtering && (
              <>
                <span className={styles.footOf}>of</span>
                <span className={styles.footNum}><Figure of={read} value={total} /></span>
              </>
            )}
            <span className={styles.footLabel}>rows</span>
          </span>
          <span className={styles.footRule} aria-hidden="true" />
          {/* Each counter is also the clause that isolates it: `2 faulted` is
              the answer and the fastest route to the two rows behind it. The
              typography does not change — a status bar that grows buttons
              stops being a status bar — so the affordance is the hover rule
              and the pressed state is the accent underline. */}
          {/* Four counters, one source. None of them is a measurement until
              `/api/robots` has answered, and the tone must not fire off an
              unread zero either — a steel `— faulted` is the honest reading. */}
          <CountFilter
            count={figure(read, counts.live)}
            label="live"
            on={filters.link.has('live')}
            onPick={() => { toggleFacet('link', 'live'); }}
          />
          <CountFilter
            count={figure(read, counts.faults)}
            label="faulted"
            tone={!unread(read) && counts.faults > 0 ? 'error' : undefined}
            on={filters.flag.has('fault')}
            onPick={() => { toggleFacet('flag', 'fault'); }}
          />
          <CountFilter
            count={figure(read, counts.low)}
            label={`battery under ${BATTERY_WARN}%`}
            tone={!unread(read) && counts.low > 0 ? 'warn' : undefined}
            on={filters.flag.has('lowbatt')}
            onPick={() => { toggleFacet('flag', 'lowbatt'); }}
          />
          <CountFilter
            count={figure(read, counts.never)}
            label="never reported"
            on={filters.flag.has('never')}
            onPick={() => { toggleFacet('flag', 'never'); }}
          />
          <span className={styles.footSpacer} />
          {selected.size > 0 && (
            <span className={styles.footItem} data-tone="accent">
              <span className={styles.footNum}>{selected.size}</span>
              <span className={styles.footLabel}>selected</span>
            </span>
          )}
          {/* THE FOOTER'S LIVENESS TAG, ON ITS OWN EVIDENCE.

              It was `<LiveTag live … />` — the boolean hard-coded true — so
              this strip flew `● Polling`, filled dot and all, over `0 rows ·
              0 live · 0 faulted` for the whole of a cold start and the whole
              of an outage. Every other liveness mark on the frame already
              answers off a read; this is the console's own per-source tag
              (`ReadTag`), so the footer, the panel headers and the supply
              ledger below now print one vocabulary: `Polling 5s` while reads
              land, `⊙ no reply` when they stop, `⊙ Reading 6s` while the
              first one is still out.

              `diagnose` because this tag has no fault line under it and no
              panel header beside it — it is the only thing on this strip that
              can name the state, and `Polling off` over a source that never
              answered is not that name. */}
          <ReadTag read={read} diagnose />
          <span className={styles.footAge}>
            <span className={styles.footLabel}>updated</span>
            <Timestamp
              value={
                robotsQuery.dataUpdatedAt > 0
                  ? new Date(robotsQuery.dataUpdatedAt).toISOString()
                  : null
              }
            />
          </span>
        </div>
      </div>

      {menu !== null && (
        <div
          ref={popRef}
          className={styles.pop}
          role="dialog"
          aria-label={
            menu.kind === 'filters' ? 'Filters' : menu.kind === 'columns' ? 'Columns' : 'Trend metric'
          }
          style={{ left: menu.left, top: menu.top, width: menu.width }}
        >
          {menu.kind === 'filters' && (
            <div className={styles.popCols}>
              {facetGroups.map((g) => (
                <section key={g.key} className={styles.popGroup} data-facet={g.key}>
                  <header className={styles.popHead}>
                    <span>{g.label}</span>
                    {filters[g.key].size > 0 && (
                      <button
                        type="button"
                        className={styles.popClear}
                        onClick={() => { clearFacet(g.key); }}
                      >
                        clear
                      </button>
                    )}
                  </header>
                  {g.items.map((it) => (
                    <FacetRow
                      key={it.value}
                      label={it.label}
                      tone={it.tone}
                      count={it.count}
                      peak={g.peak}
                      title={it.title}
                      checked={filters[g.key].has(it.value)}
                      onToggle={() => { toggleFacet(g.key, it.value); }}
                    />
                  ))}
                </section>
              ))}
            </div>
          )}

          {menu.kind === 'columns' && (
            <div className={styles.popList}>
              {COL_GROUPS.map((group) => (
                <section key={group} className={styles.popGroup}>
                  <header className={styles.popHead}>
                    <span>{group}</span>
                  </header>
                  {COL_META.filter((c) => c.group === group).map((c) => {
                    /* A ticked column that the window cannot hold is marked,
                       never silently missing from this list: the operator
                       ticked it, it is still in their view, and the reason it
                       is not on screen is the window — which is a fact the
                       control that owns it has to say out loud. */
                    const shed = layout.dropped.has(c.key);
                    return (
                      <label key={c.key} className={styles.colRow} data-shed={shed}>
                        <CheckBox
                          checked={tickedSet.has(c.key)}
                          onChange={() => { toggleColumn(c.key); }}
                        />
                        <span className={styles.colName}>{c.name}</span>
                        {shed && (
                          <span
                            className={styles.colShed}
                            title={`On in this view, but ${COL_W[c.key as Exclude<ColKey, 'robot' | 'trend'>] ?? trendPx}px does not fit at this window width`}
                          >
                            no room
                          </span>
                        )}
                        {c.note !== undefined && !tickedSet.has(c.key) && (
                          <span
                            className={styles.colWhere}
                            title="The Link cell prints this age beside the state it belongs to, on every row"
                          >
                            {c.note}
                          </span>
                        )}
                        {c.telemetry === true && (
                          <span className={styles.colNote} title="Reads the per-robot telemetry window">
                            <Icon name="telemetry" size={12} />
                          </span>
                        )}
                      </label>
                    );
                  })}
                </section>
              ))}
            </div>
          )}

          {menu.kind === 'trend' && (
            <div className={styles.popList} role="radiogroup" aria-label="Trend metric">
              {TRENDS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="radio"
                  aria-checked={t.key === trendKey}
                  className={styles.trendRow}
                  title={`Scale ${trendScale(t)} · readout: ${readoutName(t)}`}
                  onClick={() => {
                    setTrendKey(t.key);
                    setMenu(null);
                  }}
                >
                  <span className={styles.trendMark} data-on={t.key === trendKey}>
                    <Icon name="check" size={12} />
                  </span>
                  <span className={styles.colName}>{t.label}</span>
                  {/* Two quantities in this column print two different
                      numbers, so the menu that swaps them names which — a
                      reservoir carries a runway, everything else carries the
                      change. A readout the reader has to infer from the digits
                      is a readout with no name. */}
                  <span className={styles.trendReadout}>{readoutWord(t)}</span>
                  <span className={styles.trendScale}>{trendScale(t)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {showNew && (
        <NewRobotDialog
          onClose={() => { setShowNew(false); }}
          /* A minted token is returned in the clear exactly once and the server
             keeps only its hash, so the navigation that used to follow
             registration has to wait: leaving the surface would destroy the one
             copy of the credential. Show it, then go to the robot. */
          onCreated={(robotId, token) => {
            setShowNew(false);
            if (token !== null) setIssued({ robotId, token });
            else navigate('detail', { robotId });
          }}
        />
      )}

      {issued !== null && (
        <EnrollmentTokenDialog
          robotId={issued.robotId}
          token={issued.token.enrollment_token}
          expiresAt={issued.token.expires_at}
          onClose={() => {
            const { robotId } = issued;
            setIssued(null);
            navigate('detail', { robotId });
          }}
        />
      )}
    </SurfaceShell>
  );
}
